import { VideoSubscriptionFailures } from './video-subscription-failures.js';
import { traceCameraRemove } from '#~/device/remove-trace.js';
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { createElement } from 'react';
import { AbortError } from '@studio/js-utils/async';
import { resolveStartCloudMode } from './start-cloud-mode.js';
import { toDeviceId, type Device } from '@studio/models/device';
import type { Token } from '@studio/models/livekit';
import { STR_NULL, type T_Maybe } from '@tryiris-ai/js-types';
//import type { T_Maybe } from '@tryiris-ai/js-types';
import Emittery from 'emittery';
import { isTrialCameraLockedDevice } from '#~/api/billing/camera-access.js';
import {
  RemoteTrackPublication,
  VideoQuality,
  type DataPublishOptions,
  type RemoteParticipant,
  type RemoteTrack
} from 'livekit-client';
import { cloudReconnectPolicy, localReconnectPolicy } from './livekit-reconnect-policy.js';
import { LOCAL_RESTART_MAX_INTERVAL_MS, RestartBackoffGate } from './restart-backoff.js';
import {
  ConnectionQuality,
  ConnectionState,
  Room as LiveKitRoom,
  RoomEvent,
  //setLogLevel,
  Track
} from 'livekit-client';
import type { EventData, LiveKit, MutableTracks, Tracks } from './types.js';
// Bandwidth governor — pure policy module for progressive video degradation
// Per rule: think-in-systems (ai.knowledge/global) — governor is a clean policy
// layer that produces actions; this integration layer applies them to LiveKit
import {
  BandwidthGovernor,
  type GovernorAction,
  type DeviceGovernorState
} from './bandwidth-governor.js';
// W5: PostHog analytics events for governor — edge-only emissions, not every tick
import { BANDWIDTH_GOVERNOR_ANALYTICS } from './bandwidth-governor-analytics.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { PostHogManager } from '#~/posthog/index.js';
import { clearRoom as clearViewportRoom, getVisibleDevices } from './viewport-tracker.js';
import { getSelectedDevice } from './selection-tracker.js';
import {
  clearRoomTrackStats,
  videoStatsManagersForRoom,
  type StatsManager
} from './room-track-stats.js';
import { restoreLiveKit215AdaptiveStreamControl } from './livekit-2-15-adaptive-stream-compat.js';
import { bandwidthAttachmentDiagnostic } from './bandwidth-attachment-diagnostic.js';
//import { config } from '#~/config/index.js';
//import type { Config } from '#~/config/types.js';
import { logger } from '#~/logger/index.js';
import { nats } from '#~/nats/index.js';
import { toHexFromId, toIdFromHex } from '#~/api/utils.js';
import {
  AgentDirectLiveKitSignalGetAuthTokenRequest,
  AgentDirectLiveKitSignalGetAuthTokenResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import { generateId } from '@studio/models';
import type { AppId } from '@studio/models/app';
import { proxy } from '#~/nats/provider/nats.js';
import type { Msg } from '#~/newts/router.js';
import { startAgent } from '#~/agent/provider/start-signal.js';
import type { Agent } from '@studio/models/agent';
import { ClientType, ConnectionMode } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { CurrentUser } from '#~/auth/synchronizer.js';
//import { resetAfterModelChange } from '#~/room/provider/active-worker-registry.js';
import { setConnectionMode } from '#~/mediadevices/pipeline.js';
import { isElectronApp } from '#~/electron/utils.js';
import { trackBelongsToAgent } from './track-routing.js';
import { ElectronAPI } from '#~/electron/index.js';
// ENG-4154: read the IdentityState store, not a per-consumer bridge poll.
import { resolveLinkedAgentId } from '#~/electron/index.js';
import { getUserIsActive, onUserBecomeActive, onUserBecomeInactive } from './user-activity.js';
import { broker } from '#~/agent/broker.js';
import {
  runLiveKitConnectionDiagnostics,
  type LiveKitConnectionDiagnosticSummary
} from './connection-diagnostics.js';
import { LiveKitStartupProbeScheduler } from './startup-probe.js';
// ENG-3716: waiting-for-video-track state machine — resolves on the expected
// subscribe, times out into a cause-keyed failure UI state otherwise.
import { noteReconnectForPendingVideoTrackWaits, resolveVideoTrackWait } from './track-wait.js';

import * as persistenceLayer from '#~/persistence/index.js';
import { locateProxy } from '#~/agent/utils.js';
import type { Proxy } from '#~/agent/types.js';
import { clear, post } from '#~/notifications/utils.js';

// Bridge-restart recovery 2026-08-25: cloud retries forever (stable URL);
// local rooms give up fast and hand off to the fresh-token rebuild, because
// a restarted SFU listens on NEW random ports and the old URL is dead
// forever. See livekit-reconnect-policy.ts for the full rationale.

export let visibleOnline = true;

// The Retry control is the design-system Button, loaded on demand rather than
// imported at module scope. A static import puts the design-system graph into the
// module graph of every suite that imports this transport, and with it the
// @studio/react-utils/hooks barrel, whose animation and interval helpers
// value-import toBoolean from @tryiris-ai/js-types. Suites that replace js-types
// with a partial mock then fail to link before any test runs. Deferring the import
// keeps this transport free of UI at load time while the notice still renders the
// real Button. Per rule: think-in-systems (ai.knowledge/global).
// Caches the resolved component, not the promise, so a failed load leaves the
// binding undefined and the next attempt imports again instead of replaying a
// rejection forever.
let retryButton: (typeof import('@studio/design-system/button'))['Button'] | undefined;

const loadRetryButton = async (): Promise<
  (typeof import('@studio/design-system/button'))['Button']
> => {
  retryButton ??= (await import('@studio/design-system/button')).Button;
  return retryButton;
};

type GovernorSuppressionReason = 'page-hidden' | 'reconnecting' | 'video-blocked' | 'disconnected';

// Bounded local-video retry window before media fails over to cloud. State
// transports stay dual-live; this cap is video-only. Do not retune client
// inactivity timers (5s/10s stay). Per Hunter T4 video contract.
const LOCAL_VIDEO_RETRY_LIMIT = 3;

export const generateLocalTokenAndGetIPsElectronIPC = async (
  identity: string,
  room: string,
  retry: number = 5,
  signal?: AbortSignal
): Promise<AgentDirectLiveKitSignalGetAuthTokenResponse> => {
  if (signal?.aborted) throw new AbortError(signal.reason);

  try {
    const data = await ElectronAPI.generateLocalTokenAndGetURIs(identity, room);
    if (signal?.aborted) throw new AbortError(signal.reason);

    const resp = AgentDirectLiveKitSignalGetAuthTokenResponse.fromPartial(data);

    return resp;
  } catch (error) {
    if (signal?.aborted || retry === 0) {
      throw error;
    }

    return generateLocalTokenAndGetIPsElectronIPC(identity, room, retry - 1, signal);
  }
};

// @ts-ignore
window.__newAutoTrackingIdentity = async () => {
  const id = generateId();
  const agentBridgeID = await resolveLinkedAgentId();

  const tokenResp = await generateLocalTokenAndGetIPsElectronIPC(id, agentBridgeID!);

  return {
    id,
    ...tokenResp
  };
};

export const generateLocalTokenAndGetIPs = async (
  encoderId: Uint8Array,
  identity: string,
  room: string,
  retry: number = 5,
  signal?: AbortSignal,
  ownedDeadline = false
): Promise<AgentDirectLiveKitSignalGetAuthTokenResponse> => {
  if (signal?.aborted) throw new AbortError(signal.reason);

  if (isElectronApp()) {
    const agentBridgeID = await resolveLinkedAgentId();
    if (signal?.aborted) throw new AbortError(signal.reason);

    const currentEncoderID = toHexFromId(encoderId)!;

    if (agentBridgeID === currentEncoderID) {
      const result = await generateLocalTokenAndGetIPsElectronIPC(identity, room, retry, signal);

      return result;
    }
  }

  const responsePromise = nats.agentConnection(toHexFromId(encoderId)!).request(
    `direct.authenticated.${toHexFromId(encoderId)!}.signal.tunnel.auth`,
    AgentDirectLiveKitSignalGetAuthTokenRequest.encode(
      AgentDirectLiveKitSignalGetAuthTokenRequest.fromPartial({
        room: toIdFromHex(room)!,
        identity: identity,
        roomJoin: true
      })
    ).finish()
  );

  let timeout: NodeJS.Timeout | undefined;
  let abortListener: (() => void) | undefined;

  // Tie retry timers to caller teardown so detached acquisition cannot keep polling.
  // Per rule: think-in-systems — explicit lifecycle ownership.
  const timeoutPromise = new Promise((resolve, reject) => {
    // The recovery episode owns its deadline. Racing a shorter token timeout
    // would release this flight while the Router request is still pending.
    if (ownedDeadline) return;
    timeout = setTimeout(() => {
      if (retry === 0) {
        reject(new Error('Timeout while waiting for local token.'));
      } else {
        generateLocalTokenAndGetIPs(encoderId, identity, room, retry - 1, signal).then(
          resolve,
          reject
        );
      }
    }, 1000);
  });
  const aborted = signal
    ? new Promise<never>((_resolve, reject) => {
        abortListener = () => reject(new AbortError(signal.reason));
        signal.addEventListener('abort', abortListener, { once: true });
        if (signal.aborted) abortListener();
      })
    : undefined;

  try {
    const response = await Promise.race(
      aborted ? [responsePromise, timeoutPromise, aborted] : [responsePromise, timeoutPromise]
    );

    if (typeof response === 'string') {
      throw new Error(response);
    }

    const resp = AgentDirectLiveKitSignalGetAuthTokenResponse.decode((<Msg>response).data);

    return resp;
  } finally {
    clearTimeout(timeout);
    if (abortListener) signal?.removeEventListener('abort', abortListener);
  }
};

/*
 * Stall-driven subscription resets (resetDeviceCloudSubscription) pace with
 * per-device exponential backoff. Fixed 2s pacing let per-tile stall recovery
 * lock into a drop/re-subscribe loop under severe congestion: every reset
 * demands an SFU keyframe the starved link cannot deliver before the next
 * reset fires, so playback never stabilizes.
 * Per rule: think-in-systems (ai.knowledge/global) — stall recovery must not
 * fight the bandwidth governor's congestion management.
 */
const STALL_RESET_BASE_INTERVAL_MS = 2_000;
const STALL_RESET_MAX_INTERVAL_MS = 30_000;
// ENG-4665 H2: livekit-client ensurePublisherConnected can busy-wait
// peerConnectionTimeout (~15s) on a room whose signal says Connected but the
// publisher PC is dead. Bound each leg so a wedged publish cannot stall
// mutate→state-sync unboundedly (router.request used to await publish BEFORE
// arming its reply timer).
const PUBLISH_LEG_TIMEOUT_MS = 2_500;
const PUBLISH_LEG_CIRCUIT_OPEN = 3;

async function awaitPublishLeg(
  promise: Promise<void>,
  label: string,
  timeoutMs: number = PUBLISH_LEG_TIMEOUT_MS
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} publish timed out after ${timeoutMs}ms`)),
          timeoutMs
        );
      })
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function publisherPcManagerClosed(room: unknown): boolean {
  if (room == null || typeof room !== 'object') {
    return false;
  }
  const engine = (room as { engine?: { pcManager?: unknown } }).engine;
  if (engine === undefined) {
    return false;
  }
  return engine.pcManager == null;
}
// Quiet period after which backoff returns to base — a later stall is a new event.
const STALL_RESET_BACKOFF_DECAY_MS = 60_000;
// Floor while the governor reports congestion: wider than the governor's 8s
// all-healthy exit window plus keyframe latency, so recovery can complete.
const STALL_RESET_CONGESTED_MIN_INTERVAL_MS = 15_000;
// Per C2/D3 (engineer-spec §5): 120s of paced attempts before the episode posts
// its "connection unavailable" notice. This is a PACING boundary, not a terminal
// state — after it the episode keeps a slow heartbeat rather than latching dead.
const LOCAL_RECOVERY_BUDGET_MS = 120_000;

/** LiveKit proxy implementation. */

export class LiveKitImpl extends Emittery<EventData> implements LiveKit {
  public readonly id: LiveKit['id'];

  /** Audio tracks. */
  #audioLocal: MutableTracks = new Map();

  // Retired publication objects must stay excluded from every resubscribe path.
  // Weak references let LiveKit reclaim them after unpublish/room teardown.
  #stalePublications = new WeakSet<RemoteTrackPublication>();
  #videoSubscriptionFailures = new VideoSubscriptionFailures();

  /** Audio tracks. */
  #audioCloud: MutableTracks = new Map();

  /** LiveKit room. */
  #liveKitRoomCloud: T_Maybe<LiveKitRoom>;

  /** LiveKit room. */
  #liveKitRoomsLocal: Record<string, LiveKitRoom> = {};

  /** Indicates that track synchronization has been started. */
  #startedLocalMap: Record<string, number | undefined> = {};
  #startingLocalMap: Record<string, boolean> = {};

  /** Indicates that track synchronization has been started. */
  #startedCloud: T_Maybe<number> = undefined;
  #startingCloud: boolean = false;

  /** Connection token. */
  #token: Token;
  #endpoint: string;

  /**
   * ENG-4174 / ENG-4247: a separate LiveKit token carrying a DISTINCT participant
   * identity, used ONLY by the startup diagnostic probe. The probe runs a real
   * ConnectionCheck (a genuine SFU join); if it used #token it would join with the
   * live participant's identity and the SFU would evict the live session via
   * DisconnectReason.DUPLICATE_IDENTITY, permanently killing the cloud transport.
   * Minted alongside #token in livekit/broker.ts. Undefined → the probe is skipped
   * (never fall back to #token). Per rule: reference-rules-in-comments (ai.knowledge/global).
   */
  #diagnosticToken: Token | undefined;
  #startupProbeScheduler: LiveKitStartupProbeScheduler | null = null;

  /** Video tracks. */
  #videoLocal: MutableTracks = new Map();

  /** Video tracks. */
  #videoCloud: MutableTracks = new Map();

  #localEncoderIds: Uint8Array[] = [];

  #preferenceMode: 'local' | 'cloud' = isElectronApp() ? 'local' : 'cloud';
  #mode: Record<string, 'local' | 'cloud'> = {};
  // Session intent. Sticky until switch.tsx setMode. T4 per-agent setMode
  // must not write this. Web can never reach deny.
  // Per rule: assess-cost-and-policy-exposure-on-gating-changes (ai.knowledge/global)
  #cloudVideoPolicy: 'allow' | 'deny' = 'allow';
  #videoPaused: Record<string, boolean> = {};

  // Per-agent listeners: a single slot dropped every agent but the last
  // NewtsConnection.init(), so reconnect/data never reached the others.
  #eventHandlers = new Map<RoomEvent, Map<string, (...args: any[]) => void>>();

  #appId: AppId;

  #focusListener: () => void;
  #focusListenerTimeout: NodeJS.Timeout | undefined;

  // Per architecture-principles: named listener reference required for cleanup in stop()
  #visibilityChangeListener: (() => void) | null = null;
  #userActivityUnsubscribers: Array<() => void> = [];
  #subscriptionGovernorInterval: ReturnType<typeof setInterval> | null = null;
  #metadataInterval: ReturnType<typeof setInterval> | null = null;
  #forceVideoReattachTimeout: ReturnType<typeof setTimeout> | null = null;

  #deviceIdLookup: (deviceId: string) => T_Maybe<Uint8Array>;

  #consecutiveSendFailureMapLocal: Record<string, number> = {};
  #consecutiveSendFailureMapCloud: Record<string, number> = {};

  // Bandwidth governor — feature-flag gated progressive video degradation.
  // When disabled, behavior is identical to pre-governor code (no adaptiveStream, no dynacast).
  // Per rule: architecture-principles (ai.knowledge/global) — modular subsystem
  // behind a flag; cloud-only (LAN is not bandwidth-constrained).
  #bandwidthGovernorEnabled: boolean;
  #governor: BandwidthGovernor | null = null;
  #selectedVideoDeviceId: Device['id'] | null = null;
  #governorReconnectSequence: Promise<void> = Promise.resolve();
  #governorSampleFloor = new Map<Device['id'], number>();
  #governorHealthGenerations = new Map<
    Device['id'],
    {
      track: RemoteTrack | undefined;
      statsGeneration: number;
      streamState: RemoteTrack['streamState'] | undefined;
      generation: number;
    }
  >();
  #governorPublicationIdentities = new Map<
    Device['id'],
    { publication: RemoteTrackPublication; track: RemoteTrack | undefined }
  >();
  #stopped = false;
  #stopController = new AbortController();
  #localRoomListenersAttached: Record<string, boolean> = {};
  #stoppingLocalMap: Record<string, boolean> = {};
  #videoRetryCount: Record<string, number> = {};
  #videoReconnecting: Record<string, boolean> = {};
  // Sticky after terminal failover: video stays on cloud until the user
  // toggles. Dual state still rebuilds the local room.
  #videoFailedOver: Record<string, boolean> = {};
  #localRebuildFailures: Record<string, number> = {};
  // Re-mint seam: the SDK reconnectPolicy never returns null, so Disconnected
  // means the engine closed (expired token / NotAllowed). Rebuild needs a fresh
  // LiveKit credential, not the constructor's one-shot token.
  #mintCloudToken: () => Promise<{ token: Token; endpoint: string }>;
  #rebuildingCloud = false;
  // Single-flight guard for #rebuildLocalAfterDisconnect: a Disconnected event
  // and a power:resume kick that land together must produce ONE startLocal
  // attempt and ONE failure count, not two (SSR-M4 integration).
  #rebuildingLocal: Record<string, Promise<void> | undefined> = {};
  #localRecoveryStartedAt: Record<string, number | undefined> = {};
  #localRecoveryAborts: Record<string, AbortController | undefined> = {};
  #localDisconnects = new WeakMap<LiveKitRoom, Promise<void>>();

  // Governor suppression reason-set tracks overlapping lifecycle ownership.
  // Multiple reasons can overlap (page hidden + reconnect). The governor only
  // resumes when ALL reasons are cleared, preventing premature unblock.
  // Per rule: think-in-systems (ai.knowledge/global) — clean ref-count model
  #governorSuppressionReasons = new Set<GovernorSuppressionReason>();

  // Last emitted tick-skip stamp; log-on-change keeps the 1s tick from
  // flooding while still recording every suppression-state transition.
  #lastGovernorSkipNote: string | undefined;

  /**
   * Construct the LiveKit proxy.
   *
   * @param liveKitId - Connection identifier.
   * @param token - LiveKit connection token.
   * @param appId - Application identifier.
   * @param localEncoderId - Local encoder identifier (optional).
   * @param bandwidthGovernorEnabled - Whether the bandwidth governor is active.
   * @param diagnosticToken - Optional distinct-identity token for the ENG-3868 startup probe (web/cloud only).
   * @param mintCloudToken - Optional re-mint for in-place cloud rebuild after the SDK gives up.
   */
  public constructor(
    liveKitId: LiveKit['id'],
    token: Token,
    endpoint: string,
    appId: AppId,
    localEncoderIds: Uint8Array[],
    mode: 'local' | 'cloud',
    deviceIdLookup: (deviceId: string) => T_Maybe<Uint8Array>,
    bandwidthGovernorEnabled: boolean = false,
    diagnosticToken?: Token,
    mintCloudToken?: () => Promise<{ token: Token; endpoint: string }>
  ) {
    super();

    this.#deviceIdLookup = deviceIdLookup;
    this.id = liveKitId;
    this.#token = token;
    this.#endpoint = endpoint;
    this.#bandwidthGovernorEnabled = bandwidthGovernorEnabled;
    this.#diagnosticToken = diagnosticToken;
    // Tests that construct LiveKitImpl directly omit the seam; fall back to the
    // credentials already on the instance so rebuild is still exercisable.
    this.#mintCloudToken =
      mintCloudToken ?? (async () => ({ token: this.#token, endpoint: this.#endpoint }));
    //this.#liveKitDeviceResolver = liveKitDeviceResolver;

    // When the bandwidth governor is enabled, turn on LiveKit's adaptive stream
    // and dynacast features to let the SFU help manage bandwidth alongside our
    // client-side governor. When disabled, preserve current behavior exactly.
    // Per rule: architecture-principles (ai.knowledge/global) — cloud-only;
    // local mode rooms always use adaptiveStream: false below.
    this.#liveKitRoomCloud = new LiveKitRoom({
      adaptiveStream: bandwidthGovernorEnabled,
      dynacast: bandwidthGovernorEnabled,
      reconnectPolicy: cloudReconnectPolicy
    });

    // Instantiate the governor when feature-flagged on. It runs as a pure policy
    // module — produces actions that #runGovernorTick() applies to publications.
    if (bandwidthGovernorEnabled) {
      this.#governor = new BandwidthGovernor();
      // Seed last-known selection before the first tick: the React sync only
      // re-pushes after broker registration, several ticks after construction,
      // and an unseeded governor pauses the selected tile like any other.
      this.#selectedVideoDeviceId = getSelectedDevice(this.id);
      this.#governor.setSelectedDeviceId(this.#selectedVideoDeviceId);
    }

    (<any>window).lkCloud = this.#liveKitRoomCloud;

    (<any>window).lk = this;

    for (const localEncoderId of localEncoderIds) {
      const hexId = toHexFromId(localEncoderId)!;

      this.#liveKitRoomsLocal[hexId] = new LiveKitRoom({
        adaptiveStream: false,
        reconnectPolicy: localReconnectPolicy
      });
      this.#localEncoderIds.push(localEncoderId);
      const localEncoderIdHex = toHexFromId(localEncoderId)!;
      this.#mode[localEncoderIdHex] = mode;
    }

    // A studio with an embedded (cloud-only) source defaults to Cloud: its desktop
    // bridges stay on cloud until the operator picks Local or a Local lock applies.
    if (localEncoderIds.some((id) => this.#isEmbeddedAgent(id))) {
      for (const localEncoderId of localEncoderIds) {
        if (this.#isEmbeddedAgent(localEncoderId)) continue;
        const localEncoderIdHex = toHexFromId(localEncoderId)!;
        this.#cloudByDefault.add(localEncoderIdHex.toLowerCase());
        this.#mode[localEncoderIdHex] = 'cloud';
      }
    }

    this.#appId = appId;

    //setLogLevel(this.#config.isProduction ? 'warn' : 'info');

    this.#focusListener = async () => {
      clearTimeout(this.#focusListenerTimeout);

      if (document.hidden) {
        this.#focusListenerTimeout = setTimeout(
          async () => {
            // Check if the document has become visible since the event was triggered.
            if (!document.hidden) {
              return;
            }

            this.blockAllVideo();

            visibleOnline = false;
            this.#refreshMetadata();

            // Check if the document has become visible since the event was triggered.
            if (!document.hidden) {
              this.#focusListener();
            }
          },
          2 * 60 * 1000
        ); // 2 minutes
        return;
      }

      this.unblockAllVideo();

      visibleOnline = true;
      this.#refreshMetadata();

      this.#watchAgentPresence();
    };

    this.#userActivityUnsubscribers.push(
      onUserBecomeActive(() => {
        this.#refreshMetadata();

        for (const agent of broker) {
          agent.forcePing();
        }
      }),
      onUserBecomeInactive(() => {
        this.#refreshMetadata();

        for (const agent of broker) {
          agent.forcePing();
        }
      })
    );

    // W3: Governor-specific page visibility listener. Unlike the existing
    // focusListener (2-min delay), the governor suspends immediately on hidden
    // to stop evaluating tracks the user can't see.
    // Per rule: architecture-principles — works in browser + Electron
    if (this.#bandwidthGovernorEnabled && typeof document !== 'undefined') {
      // Per architecture-principles: named listener reference required for cleanup in stop()
      this.#visibilityChangeListener = () => {
        if (document.hidden) {
          this.#suspendGovernorFor('page-hidden');
        } else {
          this.#resumeGovernorFor('page-hidden');
        }
      };
      document.addEventListener('visibilitychange', this.#visibilityChangeListener);
      if (document.hidden) {
        this.#suspendGovernorFor('page-hidden');
      }
    }

    this.#subscriptionGovernorInterval = setInterval(() => {
      void this.#runSubscriptionAndGovernorCycle();
    }, 1000);

    this.#metadataInterval = setInterval(() => {
      this.#refreshMetadata();
    }, 5000);

    // Interface-switch incident 2026-08-25: when an interface disappears
    // (ethernet unplugged / wifi off), sockets bound to it blackhole
    // silently — livekit only notices after 10s+ ICE / signal-ping
    // timeouts, freezing state and flapping the bridge offline. Electron
    // main watches the OS interface table (network_watch.ts) and tells us
    // the instant it changes; resume every connected room immediately so
    // recovery starts at the change, not at the timeout. Browser builds
    // have no such signal and keep the timeout-driven behavior
    // (architecture-principles: Electron/browser parity — additive only).
    if (isElectronApp()) {
      // Keep the remover: the preload's ipcRenderer.on returns it. Without
      // this every room join leaked one listener (and one captured `this`)
      // for the process lifetime, so stopped instances kept resuming rooms.
      // Released in #clearGovernorResources (stop()).
      this.#networkChangeUnsubscribe = window.electron?.ipcRenderer?.on(
        'network:interfaces-changed',
        (_ev, delta) => {
          const lost = (delta as { lost?: string[] })?.lost ?? [];

          // Only an ADDRESS LOSS can strand established pairs; a gained
          // interface never breaks an existing connection.
          if (lost.length === 0) {
            return;
          }

          this.#resumeRoomsAfterNetworkChange();
        }
      );
    }
  }

  #networkChangeUnsubscribe: (() => void) | undefined;
  #networkResumeInFlight = false;

  /**
   * Trigger livekit's resume path (ICE restart over the surviving signaling
   * channel) on the cloud room and every local room. The local rooms'
   * signaling websockets ride loopback (survive any interface change), so
   * their resume completes in ~1s; the cloud room's resume either completes
   * over the surviving interface or fails fast and escalates to livekit's
   * own full reconnect — both beat waiting out the 10s ICE timeout.
   * One-at-a-time guard: interface flaps within a resume window ride the
   * in-flight attempt instead of stacking concurrent restarts.
   */
  async #resumeRoomsAfterNetworkChange(): Promise<void> {
    if (this.#networkResumeInFlight) {
      return;
    }
    this.#networkResumeInFlight = true;

    try {
      const rooms: LiveKitRoom[] = [
        ...(this.#liveKitRoomCloud ? [this.#liveKitRoomCloud] : []),
        ...Object.values(this.#liveKitRoomsLocal)
      ];

      await Promise.allSettled(
        rooms
          .filter((room) => room.state === ConnectionState.Connected)
          .map((room) =>
            // 'signal-reconnect' closes the signal socket so livekit-client
            // runs its own reconnect path (signal resume + ICE restart) with
            // participant identity preserved, so presence never drops
            // server-side. NOT 'resume-reconnect': in livekit-client 2.15.2
            // that scenario calls engine.failNext() first, so the first
            // resume attempt is guaranteed to throw 'simulated failure' and
            // recovery only starts on the reconnect policy's next delay.
            room.simulateScenario('signal-reconnect').catch((error) => {
              logger.warning('[networkResume] room resume failed', error);
            })
          )
      );
    } finally {
      this.#networkResumeInFlight = false;
    }
  }

  public triggerFocusListener(): void {
    this.#focusListener();
  }

  public connectedModeForAgent(agentId: Uint8Array): 'local' | 'cloud' {
    return this.unsafeConnectedModeForAgent(agentId, false);
  }

  public unsafeConnectedModeForAgent(agentId: Uint8Array, hasLocked: boolean): 'local' | 'cloud' {
    const idHex = toHexFromId(agentId)!;

    if (
      (this.#lockedLocal[idHex] && hasLocked) ||
      (isElectronApp('tray') && this.#startedLocalMap[idHex])
    ) {
      return 'local';
    }

    const resolvedMode = this.#mode[idHex] ?? this.#preferenceMode;

    if (resolvedMode === 'local' && this.#startedLocalMap[idHex]) {
      return 'local';
    }

    if (resolvedMode === 'cloud' && this.#startedCloud) {
      return 'cloud';
    }

    if (this.#startedLocalMap[idHex]) {
      return 'local';
    }

    return 'cloud';
  }

  public get localModeCapable(): boolean {
    return Object.values(this.#startedLocalMap).some((started) => started !== undefined);
  }

  /**
   * Returns the (read-only) set of audio tracks.
   *
   * @returns Current audio tracks.
   */
  public audioForAgent(agentId: string, embedded: boolean = false): Tracks {
    if (this.#videoPaused[agentId]) {
      return new Map();
    }

    let audio = this.#audioCloud;

    if (this.connectedModeForAgent(toIdFromHex(agentId)!) === 'local') {
      audio = this.#audioLocal;
    }

    const filtered = new Map();

    for (const [id, track] of audio) {
      // embedded link recovery: route via trackBelongsToAgent so a just-linked embedded camera's
      // track is reachable while the device→agent map is still stale (state-sync
      // lag), instead of being dropped by the strict `=== agentId` compare.
      if (trackBelongsToAgent(id, agentId, embedded, toHexFromId(this.#deviceIdLookup(id)))) {
        filtered.set(id, track);
      }
    }

    return filtered;
  }

  #embedded: boolean = false;

  get embedded() {
    return this.#embedded;
  }

  public isStartingLocalMode(agentId: Uint8Array): boolean {
    const idHex = toHexFromId(agentId)!;
    // Boot preference is already 'cloud'; do not key off #mode. Only an
    // operator/governor setMode('cloud') — or the embedded-studio Cloud
    // default — cancels prestart hide so in-flight startLocal cannot re-clamp
    // metadata (CI #1509 16a47df3c5).
    if (this.#holdsCloud(idHex)) {
      return false;
    }

    const prestartedAt = this.#localModePrestartAt.get(idHex);
    const isStarting = !!prestartedAt;
    const hasStarted = !!this.#startedLocalMap[toHexFromId(agentId)!];

    const acquisitionFailed = this.#localModeAcqusitionFailed.get(toHexFromId(agentId)!);

    if (hasStarted || !isStarting || acquisitionFailed) {
      return false;
    }

    // Expire local startup so a failed LAN attempt cannot hide a valid cloud track forever.
    const startedAgo = (Date.now() - prestartedAt.getTime()) / 1000;
    const timeout = 15;

    const hasTimedOut = startedAgo > timeout;

    return !hasTimedOut;
  }

  /**
   * Returns the (read-only) set of video tracks.
   *
   * @returns Current video tracks.
   */
  public videoForAgent(agentId: string, embedded: boolean = false): Tracks {
    if (this.#videoPaused[agentId]) {
      //return new Map();
    }

    let videos = new Map();

    if (!this.isStartingLocalMode(toIdFromHex(agentId)!)) {
      for (const [key, video] of this.#videoCloud) {
        videos.set(key, video);
      }
    }

    for (const [key, video] of this.#videoLocal) {
      if (!videos.has(key) || this.connectedModeForAgent(toIdFromHex(agentId)!) === 'local') {
        videos.set(key, video);
      }
    }

    const filtered = new Map();

    for (const [id, track] of videos) {
      // embedded link recovery: see audioForAgent — embedded shadow-id fallback while the
      // device→agent map is stale, so a just-linked camera renders without a reload.
      if (trackBelongsToAgent(id, agentId, embedded, toHexFromId(this.#deviceIdLookup(id)))) {
        filtered.set(id, track);
      }
    }

    return filtered;
  }

  videoBlocked: boolean = false;

  public async blockAllVideo() {
    if (this.videoBlocked) {
      return;
    }

    console.log('Blocking all video');
    this.videoBlocked = true;

    // Suspend the governor via the reason-set so it correctly interacts with
    // other suppression reasons (page-hidden, reconnecting). Direct calls to
    // governor.onBlockAllVideo() would bypass the reason-set and cause premature
    // resume when unblockAllVideo() fires while another reason is still active.
    this.#suspendGovernorFor('video-blocked');

    this.#liveKitRoomCloud?.remoteParticipants.forEach((participant) => {
      participant.videoTrackPublications.forEach((pub) => {
        pub.setSubscribed(false);
        pub.track?.detach();
      });
    });

    for (const room in this.#liveKitRoomsLocal) {
      this.#liveKitRoomsLocal[room]?.remoteParticipants.forEach((participant) => {
        participant.videoTrackPublications.forEach((pub) => {
          pub.setSubscribed(false);
          pub.track?.detach();
        });
      });
    }
  }

  public async unblockAllVideo() {
    if (!this.videoBlocked) {
      return;
    }

    console.log('Unblocking all video');
    this.videoBlocked = false;

    await this.refreshTrackSubscriptionsForAll();

    // Resume only after base subscriptions are restored and other suppressions clear.
    this.#resumeGovernorFor('video-blocked');

    if (this.#forceVideoReattachTimeout) {
      clearTimeout(this.#forceVideoReattachTimeout);
    }
    this.#forceVideoReattachTimeout = setTimeout(() => {
      this.#forceVideoReattachTimeout = null;
      void this.forceVideoReattach();
    }, 1000);
  }

  reattachListeners = new Map<string, () => void>();

  public addReattachListener(id: string, listener: () => void) {
    this.reattachListeners.set(id, listener);
  }

  public removeReattachListener(id: string) {
    this.reattachListeners.delete(id);
  }

  public async forceVideoReattach() {
    for (const listener of this.reattachListeners.values()) {
      listener();
    }
  }

  public async startCloud(): Promise<void> {
    if (this.#stopped) return;

    if (!this.#liveKitRoomCloud) {
      throw new Error('LiveKit cloud room not initialized.');
    }

    if ((this.#startedCloud ?? 0) > 0) {
      return;
    }

    if (this.#startingCloud) {
      return;
    }

    this.#startingCloud = true;

    this.#liveKitRoomCloud.on(RoomEvent.ParticipantConnected, (participant) => {
      const agentId = this.#deviceIdLookup(participant.identity);

      if (agentId === undefined) {
        // embedded link recovery: a just-linked camera can connect before the client's
        // device→agent map (deviceIdLookup / locateProxy) reflects it (state-sync
        // lag). With autoSubscribe:false, refreshTrackSubscriptions can't subscribe
        // it — its locateProxy gate skips an unknown agent — so the track was
        // never subscribed and the source stayed "Connecting…" until a reload.
        // Subscribe the participant's published tracks directly; the resulting
        // TrackSubscribed binds them by deviceId, and refreshTrackSubscriptions
        // reconciles to the correct local/cloud value once the map catches up.
        this.#subscribeUnmappedCloudTracks(participant);
        return;
      }

      this.refreshTrackSubscriptions(agentId);
      // East 1c7240: identity locates some proxy so this is not the unmapped
      // path, then refresh skips because locateProxy(identity) ≠ agent hex.
      // Subscribe-all is the same operator-cloud recovery as setMode.
      // Per rule: stay-in-scope.
      this.#subscribeAllAllowedCloudVideo(agentId);
    });

    this.#liveKitRoomCloud.on(RoomEvent.ConnectionStateChanged, (state) => {
      for (const agent of broker) {
        this.refreshTrackSubscriptions(toIdFromHex(agent.id)!);
      }
      // Governor un-zombie (CI #1486 job 98003236577): the 'disconnected'
      // suppression added by #normalizeGovernorAfterDisconnect was only ever
      // resumed on RoomEvent.Reconnected — but the eviction-restart path
      // recovers via a FRESH connect(), which fires ConnectionStateChanged:
      // Connected and never Reconnected (the SDK reserves that for its
      // internal reconnect engine; a duplicate-identity disconnect is
      // terminal). The suppression then outlived the healthy room for the
      // rest of the session: every governor tick early-returned and Low
      // Bandwidth Mode silently did nothing (manualMode=true, deviceState
      // stuck 'normal'). Resume through the same recovery queue whenever a
      // fresh connect lands while a disconnect-shaped suppression is held.
      if (
        state === ConnectionState.Connected &&
        (this.#governorSuppressionReasons.has('disconnected') ||
          this.#governorSuppressionReasons.has('reconnecting'))
      ) {
        this.#queueGovernorReconnectRecovery();
      }
    });

    // embedded link recovery: a source linked AFTER the room connected publishes its track via
    // TrackPublished. With autoSubscribe:false and no handler here, that track was
    // never subscribed (TrackSubscribed never fired) → the source stayed
    // "Connecting…" until a reload. Subscribe it now: by agent when the map knows
    // it, else directly (state-sync lag). Per rule: reference-rules-in-comments.
    this.#liveKitRoomCloud.on(RoomEvent.TrackPublished, (_publication, participant) => {
      const agentId = this.#deviceIdLookup(participant.identity);

      if (agentId !== undefined) {
        this.refreshTrackSubscriptions(agentId);
        // Encoder publishes after CONNECTION_MODE_CLOUD_AND_LOCAL mutate.
        // setMode subscribe-all can run before the pub exists; refresh then
        // skips agent-identity pubs (CI #1509 1c7240 :184 encoder=3
        // track=false). Per rule: stay-in-scope.
        this.#subscribeAllAllowedCloudVideo(agentId);
        return;
      }

      this.#subscribeUnmappedCloudTracks(participant);
    });

    // Attach event listeners.
    this.#liveKitRoomCloud.on(
      RoomEvent.TrackSubscribed,
      this.#handleTrackSubscribed('cloud').bind(this)
    );

    this.#liveKitRoomCloud.on(
      RoomEvent.TrackUnsubscribed,
      this.#handleTrackUnsubscribed('cloud').bind(this)
    );

    this.#liveKitRoomCloud.on(RoomEvent.Reconnecting, () => {
      logger.info(`The LiveKit room ${this.id} is reconnecting.`);
      // W3: Suspend governor during reconnect — tracks are unreliable
      this.#suspendGovernorFor('reconnecting');
    });

    this.#liveKitRoomCloud.on(RoomEvent.Reconnected, () => {
      logger.info(`The LiveKit room ${this.id} has reconnected.`);
      // ENG-3716: a reconnect during a pending connect wait means a later
      // timeout is publisher churn ("stream interrupted"), not a dead camera.
      noteReconnectForPendingVideoTrackWaits();
      this.#queueGovernorReconnectRecovery();
    });

    this.#liveKitRoomCloud.on(RoomEvent.Disconnected, () => {
      logger.warning(`The LiveKit room ${this.id} has been disconnected.`);
      this.#normalizeGovernorAfterDisconnect();
      // SDK reconnectPolicy never returns null, so this is a closed-engine path
      // (expired token / NotAllowed). Re-mint and reconnect in place.
      void this.#rebuildCloud();
    });

    this.#liveKitRoomCloud.on(RoomEvent.TrackStreamStateChanged, () => {
      if (this.#bandwidthGovernorEnabled) {
        this.#runGovernorTick();
      }
    });

    // ENG-3868 §3 / ENG-3942 / spec §6.3-§6.6 — schedule a deferred, cloud-only,
    // per-app-launch LiveKit startup diagnostic ~5s after Connected.
    //
    // ENG-4174 / ENG-4247: the probe MUST use #diagnosticToken (a DISTINCT
    // participant identity), NOT #token. The diagnostic runs a real ConnectionCheck
    // (genuine SFU join); with the live token its join shares the live participant's
    // identity and the SFU evicts the live session via DUPLICATE_IDENTITY (terminal:
    // engine _isClosed, reconnectAttempts=0, bypassing reconnectPolicy) → permanently
    // dead cloud transport → state-sync mutation timeouts → web boot wedge. Reusing
    // the live identity here was the 2026-05-27 (#571) regression. If the diagnostic
    // token is absent, SKIP the probe rather than fall back to #token.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    if (this.#diagnosticToken) {
      this.#startupProbeScheduler?.dispose();
      this.#startupProbeScheduler = new LiveKitStartupProbeScheduler({
        room: this.#liveKitRoomCloud,
        mode: this.#preferenceMode,
        url: this.#endpoint,
        token: this.#diagnosticToken,
        roomId: this.id
      });
      this.#startupProbeScheduler.attach();
    }

    this.#liveKitRoomCloud.on(RoomEvent.ParticipantConnected, (participant) => {
      this.dispatchRoomEvent(RoomEvent.ParticipantConnected, participant);

      if (this.#localEncoderIds.some((id) => toHexFromId(id) === participant.identity)) {
        this.#watchAgentPresence();
      }
    });

    this.#liveKitRoomCloud.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.dispatchRoomEvent(RoomEvent.ParticipantDisconnected, participant);

      if (this.#localEncoderIds.some((id) => toHexFromId(id) === participant.identity)) {
        this.#watchAgentPresence();
      }
    });

    // Connect to LiveKit
    try {
      await this.#liveKitRoomCloud.connect(this.#endpoint, this.#token, {
        autoSubscribe: false
      });
    } catch (error) {
      // Teardown-driven connect rejection must not launch a stale diagnostic participant.
      if (!this.#stopped) {
        this.#runConnectionDiagnostics('cloud', 'cloud-connect-failed', undefined, error);
      }
      throw error;
    }

    if (this.#stopped) {
      await this.stopCloud();
      return;
    }

    this.#liveKitRoomCloud.on(RoomEvent.DataReceived, (data, participant) => {
      this.dispatchRoomEvent(RoomEvent.DataReceived, data, participant);

      const isAgent = this.#localEncoderIds.some((id) => participant?.identity === toHexFromId(id));

      if (isAgent) {
        const agentID = toIdFromHex(participant?.identity)!;

        this.monitorPacketReception(agentID, 'cloud');
      }
    });

    this.#liveKitRoomCloud.on(RoomEvent.ConnectionStateChanged, (state) => {
      // S4: forward from every live state transport. Dual rooms stay up;
      // gating on #mode dropped msg-buffer flush on the other leg.
      this.dispatchRoomEvent(RoomEvent.ConnectionStateChanged, state);
    });

    this.#refreshMetadata();

    this.#startedCloud = Date.now();
    this.#startingCloud = false;

    // Setup NUTS
    for (const localEncoderId of this.#localEncoderIds) {
      proxy.connectNewts(toHexFromId(localEncoderId)!, this);
    }

    (<any>window).cloudLK = this.#liveKitRoomCloud;

    /*
     * Perform an initial synchronization of the room.  We do this because we often do not receive the
     * `TrackSubscribed` event for tracks that are already present in the room when we connect.
     */
    this.#synchronize();

    this.emit('started').catch((error) => {
      logger.critical(error);
    });
  }

  #localModeAcqusitionFailed: Map<string, boolean> = new Map();

  public async acquireLocalConnectionWithRetry(
    agentId: Uint8Array,
    maxRetry: number = 15,
    current: number = 0,
    signal: AbortSignal = this.#stopController.signal
  ): Promise<{
    localToken: string;
    localBridgeUrl: string;
    uris: string[];
    caCertificate: Uint8Array;
  }> {
    const waitForStop = async <T>(operation: Promise<T>): Promise<T> => {
      if (signal.aborted) {
        void operation.catch(() => undefined);
        throw new AbortError('LiveKit stopped during local acquisition.');
      }

      let abortListener: (() => void) | undefined;
      const stopped = new Promise<never>((_resolve, reject) => {
        abortListener = () => {
          reject(new AbortError('LiveKit stopped during local acquisition.'));
        };
        signal.addEventListener('abort', abortListener, { once: true });
        if (signal.aborted) abortListener();
      });

      try {
        return await Promise.race([operation, stopped]);
      } finally {
        if (abortListener) signal.removeEventListener('abort', abortListener);
      }
    };

    // if the url has ?linked=true, we need to at the end then we need to add the agentId
    const url = new URL(window.location.href);
    const linked = url.searchParams.get('linked');

    const justLinkedStr =
      (await waitForStop(persistenceLayer.get('just_linked_until'))) ?? STR_NULL;
    const justLinkedAt = parseInt(justLinkedStr, 10);

    const justLinked = justLinkedAt > Date.now();

    if ((linked || justLinked) && maxRetry > 0) {
      maxRetry = 50;
    }

    const recovering = this.#localModeRecovery[toHexFromId(agentId)!] === true;
    try {
      const {
        token: localToken,
        uris,
        caCertificate
      } = await waitForStop(
        generateLocalTokenAndGetIPs(agentId, this.#appId, this.id, 0, signal, recovering)
      );

      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<undefined>((resolve) => {
        // URI preparation shares the episode deadline too; a still-pending
        // probe must not create another token/probe flight on the next tick.
        if (recovering) return;
        timeoutHandle = setTimeout(
          () => {
            resolve(undefined);
          },
          current < 5 ? 2_000 : 20_000
        ); // set a low timeout for the first attempt
      });

      const localBridgeUrlPromise = isElectronApp()
        ? ElectronAPI.prepareLocalBridge(uris, caCertificate)
        : Promise.resolve(uris[0]);

      let localBridgeUrl: string | undefined;
      try {
        localBridgeUrl = await waitForStop(Promise.race([timeout, localBridgeUrlPromise]));
      } finally {
        clearTimeout(timeoutHandle);
      }

      if (!localBridgeUrl) {
        throw new Error('No local bridge URL.');
      }

      return { localToken, localBridgeUrl, uris, caCertificate };
    } catch (error) {
      if (signal.aborted || current >= maxRetry) {
        throw error;
      }

      this.#localModeAcqusitionFailed.set(toHexFromId(agentId)!, true);
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        await waitForStop(
          new Promise<void>((resolve) => {
            retryTimer = setTimeout(resolve, 1_000);
          })
        );
      } finally {
        clearTimeout(retryTimer);
      }

      return this.acquireLocalConnectionWithRetry(agentId, maxRetry, current + 1, signal);
    } finally {
      if (!this.#stopped && !signal.aborted) this.#refreshMetadata();
    }
  }

  #localModePrestartAt: Map<string, Date> = new Map();
  // Distinct from #mode: constructor preference is already 'cloud'.
  #operatorRequestedCloud: Set<string> = new Set();
  // Desktop bridges held on Cloud because the studio also has an embedded
  // (cloud-only) source; lowercase hex. The operator's own mode choice or a
  // Local lock releases a hold.
  #cloudByDefault: Set<string> = new Set();

  // Standing cloud request auto-local paths defer to: operator Cloud or the embedded-studio default.
  #holdsCloud(idHex: string): boolean {
    return this.#operatorRequestedCloud.has(idHex) || this.#cloudByDefault.has(idHex.toLowerCase());
  }

  /** True while any desktop bridge is held on the embedded-studio Cloud default. */
  public holdsCloudByDefault(): boolean {
    return this.#cloudByDefault.size > 0;
  }

  // A Local lock (admin padlock or billing) ends the Cloud default: the bridge goes
  // local exactly as its local room's Connected timer would have, prestart included.
  #releaseCloudDefault(agentIdHex: string): void {
    if (!this.#cloudByDefault.delete(agentIdHex.toLowerCase())) return;
    const agentId = toIdFromHex(agentIdHex);
    if (!agentId) return;
    const idHex = toHexFromId(agentId)!;
    if (!this.#startedLocalMap[idHex] && !this.#localModePrestartAt.has(idHex)) {
      this.#localModePrestartAt.set(idHex, new Date());
    }
    void this.setMode('local', agentId, false, true).catch((error) => {
      logger.warning(`lock setMode(local) failed agent=${idHex}`, error);
    });
  }

  /**
   * Serialized start/stop (race fix 2026-08-25): startLocal and stopLocal
   * used to run concurrently from four actors (disconnect rebuild, presence
   * recovery, paced recovery loop, retryLocalVideo). startLocal only took
   * its single-flight guard AFTER the multi-second token+URI acquire, and
   * stopLocal's finally force-cleared that guard and REPLACED the Room
   * object — interleavings produced duplicate listener attachment (state
   * transactions applied twice), concurrent connect() on one Room, or a
   * connected-but-orphaned Room whose media and data went nowhere while the
   * UI read the fresh replacement: sources dark + state dead + "connected"
   * shown. All local start/stop work now runs through a per-agent promise
   * queue, making every interleaving sequential.
   */
  #localOpQueues: Record<string, Promise<void>> = {};
  // Every start still in flight or queued for the agent. A single slot only
  // aborted the NEWEST start: with two queued (rebuild + presence recovery
  // after one Disconnected) stopLocal released the chain via the second's
  // abort, ran, and the first then finished connect() on the old Room — the
  // connected-but-orphaned Room the queue exists to prevent.
  #localStartAborts: Record<string, Set<AbortController> | undefined> = {};

  #enqueueLocalOp(
    idHex: string,
    op: () => Promise<void>,
    abortable?: AbortController
  ): Promise<void> {
    const prev = this.#localOpQueues[idHex] ?? Promise.resolve();
    const run = prev.then(op, op);
    // Queue-advancement link. A hung START must not wedge the queue — the
    // characterization contract is that stop() completes while a local
    // connect is still pending and the late start self-cancels (post-connect
    // #stopped/abort guard). An abortable op therefore releases the chain
    // the moment it is aborted; its body keeps running detached and bails at
    // the next stage boundary. Callers still see their own op's outcome
    // through the returned promise.
    const result = abortable ? this.#waitForLocalOperation(run, abortable.signal) : run;
    // Aborting a queued start releases its caller, not a preceding raw
    // disconnect. A successor must still wait for that SDK reservation.
    this.#localOpQueues[idHex] = Promise.allSettled([prev, result]).then(() => undefined);
    return result;
  }

  #waitForLocalOperation(run: Promise<void>, signal: AbortSignal): Promise<void> {
    let onAbort: () => void;
    return Promise.race([
      run,
      new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(new AbortError('Local connection operation retired.'));
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted) onAbort();
      })
    ]).finally(() => signal.removeEventListener('abort', onAbort));
  }

  public startLocal(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    traceCameraRemove('connection.startLocal-requested', {
      agentId: idHex,
      roomId: this.id,
      caller: new Error().stack?.split('\n').slice(2, 7).join(' | ')
    });
    const controller = new AbortController();
    const inFlight = (this.#localStartAborts[idHex] ??= new Set());
    inFlight.add(controller);
    const run = this.#enqueueLocalOp(
      idHex,
      () => this.#startLocalInner(agentId, controller.signal),
      controller
    );
    void run.catch(() => undefined).finally(() => inFlight.delete(controller));
    return run;
  }

  public stopLocal(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    this.#localModeRecovery[idHex] = false;
    this.#localRecoveryNotified[idHex] = false;
    this.#localRecoveryAborts[idHex]?.abort();
    this.#localRecoveryAborts[idHex] = undefined;
    this.#rebuildingLocal[idHex] = undefined;
    this.#localRecoveryStartedAt[idHex] = undefined;
    clearTimeout(this.#localRecoveryTimers[idHex]);
    this.#localRecoveryTimers[idHex] = undefined;
    traceCameraRemove('connection.stopLocal-requested', {
      agentId: idHex,
      roomId: this.id,
      caller: new Error().stack?.split('\n').slice(2, 7).join(' | ')
    });
    // Interrupt EVERY in-flight or queued start so the stop cannot be wedged
    // behind a connect that never resolves, and no earlier start can outlive
    // the stop and connect an orphaned Room.
    for (const controller of this.#localStartAborts[idHex] ?? []) controller.abort();
    // Logical retirement must complete even if the SDK cannot finish closing.
    // The raw disconnect remains reserved in the per-agent queue.
    void this.#stopLocalInner(agentId).catch((error) => {
      logger.warning('Retired local connection cleanup failed.', error);
    });
    try {
      clear(`local-recovery-${idHex}`);
    } catch {
      /* UI observers cannot prevent teardown. */
    }
    return Promise.resolve();
  }

  async #startLocalInner(agentId: Uint8Array, abortSignal?: AbortSignal): Promise<void> {
    if (this.#stopped || abortSignal?.aborted) return;

    if (!isElectronApp()) {
      // ENG-4332: this branch deliberately does NOT call
      // reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.LOCAL_BRIDGE_CONNECTION). Every
      // caller of startLocal is the app's own connect machinery, not a user gesture:
      // the `start()` loop below runs it once per local encoder on every room entry, the
      // stall watchdog retries per agent, and `restartLocalConnection` is a reconnect
      // restore handler. Guidance emitted here would count connection attempts rather
      // than people and swamp the demand ranking the event exists to feed. The browser
      // user's real gesture for this capability is the Local Mode toggle, which emits
      // DESKTOP_ONLY_FEATURE.LOCAL_MODE from agent/components/local-mode/switch.tsx.
      // Enforced by analytics/domains/feature-gate.call-sites.test.ts.
      //
      // The rejection stays: it is load-bearing control flow (callers fall back to Cloud
      // Mode), and the message is already kept out of PostHog error groups by the
      // pre-existing noise-filter rule `electron-only-local-connection`.
      throw new Error('Local connection is only supported in the Electron app.');
    }

    const idHexForPrestart = toHexFromId(agentId)!;
    if (!this.#holdsCloud(idHexForPrestart) && !this.#localModePrestartAt.has(idHexForPrestart)) {
      this.#localModePrestartAt.set(idHexForPrestart, new Date());
    }

    if (!this.presentAgents.has(toHexFromId(agentId)!)) {
      await new Promise<void>((resolve) => {
        let unsubscribePresence = (): void => undefined;
        const settle = (): void => {
          clearTimeout(timeout);
          unsubscribePresence();
          this.#stopController.signal.removeEventListener('abort', settle);
          abortSignal?.removeEventListener('abort', settle);
          resolve();
        };
        const timeout = setTimeout(settle, 5_000);
        unsubscribePresence = this.onAgentPresence((presentAgentId) => {
          if (agentId.toString() === presentAgentId.toString()) settle();
        });
        this.#stopController.signal.addEventListener('abort', settle, { once: true });
        abortSignal?.addEventListener('abort', settle, { once: true });
        if (this.#stopped || abortSignal?.aborted) settle();
      });
    }

    if (this.#stopped || abortSignal?.aborted) return;

    const idHex = toHexFromId(agentId)!;

    try {
      const { localToken, localBridgeUrl } = await this.acquireLocalConnectionWithRetry(
        agentId,
        this.#localModeRecovery[idHex] ? 0 : 15,
        0,
        abortSignal
      );

      if (this.#stopped || abortSignal?.aborted) return;

      if (!localBridgeUrl || !localToken) {
        throw new Error('Local connection credentials are incomplete.');
      }

      if ((this.#startedLocalMap[idHex] ?? 0) > 0) {
        const room = this.#liveKitRoomsLocal[idHex];
        if (room && room.state !== ConnectionState.Disconnected) {
          return;
        }
        // Room was started but is down — fall through to re-acquire + connect.
        this.#startedLocalMap[idHex] = undefined;
      }

      if (this.#startingLocalMap[idHex]) {
        return;
      }

      this.#startingLocalMap[idHex] = true;

      const ownedRoom = this.#liveKitRoomsLocal[idHex]!;
      if (!this.#localRoomListenersAttached[idHex]) {
        // Attach event listeners.
        this.#liveKitRoomsLocal[idHex]!.on(
          RoomEvent.TrackSubscribed,
          this.#handleTrackSubscribed('local').bind(this)
        );

        this.#liveKitRoomsLocal[idHex]!.on(
          RoomEvent.TrackUnsubscribed,
          this.#handleTrackUnsubscribed('local').bind(this)
        );

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.Connected, async () => {
          if (this.#stopped || ownedRoom !== this.#liveKitRoomsLocal[idHex]) return;
          this.#watchAgentPresence();
          this.#refreshMetadata();
          // connect() (rebuild) never fires Reconnected — clear the video
          // reconnecting indicator here so the STag/governor/toast do not stick.
          this.#clearVideoReconnecting(idHex);

          if (this.#videoFailedOver[idHex]) {
            return;
          }

          setTimeout(async () => {
            // A stop() in the last second must not let a dead instance mutate
            // the encoder's connection mode.
            if (
              this.#stopped ||
              ownedRoom !== this.#liveKitRoomsLocal[idHex] ||
              ownedRoom.state !== ConnectionState.Connected
            )
              return;
            // auto=true: defer to a live operator cloud request (see setMode).
            await this.setMode('local', agentId, true, true).catch((error) => {
              logger.warning('Automatic local mode selection failed.', error);
            });
          }, 1000);
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.Reconnecting, async () => {
          logger.info(`The LiveKit room ${this.id} is reconnecting.`);
          this.#markVideoReconnecting(idHex, true);
          this.#suspendGovernorFor('reconnecting');
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.Reconnected, () => {
          logger.info(`The LiveKit room ${this.id} has reconnected.`);

          // ENG-3716: mark pending connect waits so a later timeout is keyed as
          // "stream interrupted" (publisher churn), not "camera unreachable".
          noteReconnectForPendingVideoTrackWaits();

          // Synchronize the room on reconnection.
          this.#synchronize();
          // ENG-3716: publisher churn across a reconnect can leave BOTH the old
          // and the republished publication subscribed — the source then drifts
          // into 'Multiple video tracks detected' instead of cleanly showing the
          // current stream. Cancel stale subscriptions, then reconcile the
          // desired subscription state for this agent's rooms.
          this.#cancelStaleSubscriptions();
          this.#watchAgentPresence();
          this.refreshTrackSubscriptions(agentId).catch((error) => {
            logger.critical(error);
          });
          this.#clearVideoReconnecting(idHex);
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.Disconnected, (reason) => {
          traceCameraRemove('connection.disconnected', {
            agentId: idHex,
            roomId: this.id,
            reason,
            stopping: this.#stoppingLocalMap[idHex],
            stopped: this.#stopped
          });
          logger.warning(`The LiveKit room ${this.id} has been disconnected.`);

          // SDK reconnect exhaustion needs a fresh token and URI probe: a
          // restarted bridge/SFU may advertise a different local endpoint.
          // Restore, resume, presence and terminal SDK disconnect all request
          // the same paced per-agent owner. Healthy sibling rooms stay intact.
          if (!this.#stoppingLocalMap[idHex] && !this.#stopped) {
            void this.restartLocalConnection(agentId);
          }
          void this.#watchAgentPresence();
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.DataReceived, (data, participant) => {
          if (participant?.identity === toHexFromId(agentId)) {
            this.monitorPacketReception(agentId, 'local');
          }

          // S1: always deliver; newts router message-id dedup is first-packet-wins.
          this.dispatchRoomEvent(RoomEvent.DataReceived, data, participant);
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.ConnectionStateChanged, (state) => {
          // S4: forward from both rooms regardless of #mode.
          this.dispatchRoomEvent(RoomEvent.ConnectionStateChanged, state);
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.ParticipantConnected, (p) => {
          if (this.#localEncoderIds.some((id) => toHexFromId(id) === p.identity)) {
            this.#watchAgentPresence();
          }
        });

        this.#liveKitRoomsLocal[idHex]!.on(RoomEvent.ParticipantDisconnected, (p) => {
          if (this.#localEncoderIds.some((id) => toHexFromId(id) === p.identity)) {
            this.#watchAgentPresence();
          }

          // The proxy lookup may disappear before LiveKit emits participant removal.
          const deviceId = toDeviceId(p.identity);
          const audioRemoved = this.#audioLocal.delete(deviceId);
          const videoRemoved = this.#videoLocal.delete(deviceId);
          if (audioRemoved || videoRemoved) {
            this.emit('changed').catch((error) => logger.critical(error));
          }
        });

        this.#localRoomListenersAttached[idHex] = true;
      }

      /*
      this.#liveKitRoom.on(RoomEvent.DataReceived, (_data, participant) => {
        if (!participant) {
          return;
        }

        const agentId = this.#liveKitDeviceResolver(participant.identity);

        if (agentId === undefined) {
          return;
        }

        const local = new Date();
        const payload = AgentDirectTrackTimestamp.decode(data);
        const { timestamp } = payload;

        SharedTimestampManager.setTimestampForAgent(agentId, {
          local,
          remote: timestamp
        });

      });
      */

      try {
        const liveKitRoom = this.#liveKitRoomsLocal[idHex]!;
        try {
          // R2(iii) empirical 2026-08-23: JoinResponse advertises
          // turn:127.0.0.1 with creds, but Chromium iceTransportPolicy:relay
          // on loopback signaling failed local connect (38× local-connect-failed).
          // Do not ship relay-only. W4 node_ip + W5 pion loopback still hold;
          // studio keeps host candidates (LAN↔127 observed, no VPN 100.x).
          await liveKitRoom.connect(localBridgeUrl, localToken, {
            autoSubscribe: false
          });
        } catch (error) {
          // Teardown-driven rejection must not probe with an abandoned local identity.
          if (!this.#stopped && !abortSignal?.aborted) {
            this.#runConnectionDiagnostics(
              'local',
              'local-connect-failed',
              { url: localBridgeUrl, token: localToken, agentId: idHex },
              error
            );
          }
          throw error;
        }

        if (this.#stopped || abortSignal?.aborted) {
          if (liveKitRoom.state !== ConnectionState.Disconnected) {
            await this.#disconnectLocalRoom(liveKitRoom);
          }
          return;
        }

        (<any>window).lkLocal = this.#liveKitRoomsLocal[idHex];

        /*
        const videos = this.videoForAgent(idHex);

        for (const id of videos.keys()) {
          const video = videos.get(id);
          //video?.detach();
        }
    */

        (<any>window).localLK = this.#liveKitRoomsLocal[idHex];
      } catch (error) {
        throw error;
      }

      this.#refreshMetadata();

      // Setup NUTS
      proxy.connectNewts(toHexFromId(agentId)!, this);

      traceCameraRemove('connection.started', {
        agentId: idHex,
        roomId: this.id,
        participantId: this.#liveKitRoomsLocal[idHex]?.localParticipant.identity
      });
      this.#startedLocalMap[idHex] = Date.now();
      this.#startingLocalMap[idHex] = false;
      this.#localRebuildFailures[idHex] = 0;
      this.#clearVideoReconnecting(idHex);

      /*
       * Perform an initial synchronization of the room.  We do this because we often do not receive the
       * `TrackSubscribed` event for tracks that are already present in the room when we connect.
       */
      this.#synchronize();

      this.emit('started').catch((error) => {
        logger.critical(error);
      });
    } catch (error) {
      if (!abortSignal?.aborted) this.#startingLocalMap[idHex] = false;
      throw error;
    }
  }

  /** Stops the LiveKit track synchronization. */
  public async stopCloud(): Promise<void> {
    const liveKitRoom = this.#liveKitRoomCloud;
    if (liveKitRoom) this.#videoSubscriptionFailures.forgetRoom(liveKitRoom);

    this.#startupProbeScheduler?.dispose();
    this.#startupProbeScheduler = null;

    // Remove ALL LiveKit event listeners to prevent memory leaks.
    // Previously only 4 event types were removed, but many more are registered.
    [
      RoomEvent.Disconnected,
      RoomEvent.Reconnected,
      RoomEvent.Reconnecting,
      RoomEvent.Connected,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.TrackPublished,
      RoomEvent.TrackStreamStateChanged,
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.DataReceived,
      RoomEvent.ConnectionStateChanged,
      RoomEvent.ConnectionQualityChanged
    ].forEach((event) => {
      liveKitRoom?.removeAllListeners(event);
    });

    // Disconnect connecting and reconnecting transports before ownership is released.
    try {
      if (liveKitRoom && liveKitRoom.state !== ConnectionState.Disconnected) {
        await liveKitRoom.disconnect();
      }
    } finally {
      this.#startedCloud = undefined;
      this.#startingCloud = false;

      this.emit('stopped').catch((error) => {
        logger.critical(error);
      });
    }
  }

  /** Share raw SDK cleanup without confusing its settlement with physical cancellation. */
  #disconnectLocalRoom(room: LiveKitRoom): Promise<void> {
    const pending = this.#localDisconnects.get(room);
    if (pending) return pending;
    const raw = Promise.resolve().then(async () => {
      if (room.state !== ConnectionState.Disconnected) await room.disconnect();
    });
    this.#localDisconnects.set(room, raw);
    const release = (): void => {
      if (this.#localDisconnects.get(room) === raw) this.#localDisconnects.delete(room);
    };
    void raw.then(release, release);
    return raw;
  }

  /** Retire this Room synchronously; late SDK completion cannot mutate its successor. */
  #stopLocalInner(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    traceCameraRemove('connection.stop-executing', { agentId: idHex, roomId: this.id });
    const liveKitRoom = this.#liveKitRoomsLocal[idHex];
    if (!liveKitRoom) return Promise.resolve();

    this.#videoSubscriptionFailures.forgetRoom(liveKitRoom);

    // Remove ALL LiveKit event listeners to prevent memory leaks.
    [
      RoomEvent.Disconnected,
      RoomEvent.Reconnected,
      RoomEvent.Reconnecting,
      RoomEvent.Connected,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.TrackPublished,
      RoomEvent.TrackStreamStateChanged,
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.DataReceived,
      RoomEvent.ConnectionStateChanged,
      RoomEvent.ConnectionQualityChanged
    ].forEach((event) => {
      liveKitRoom.removeAllListeners(event);
    });

    this.#startedLocalMap[idHex] = undefined;
    this.#startingLocalMap[idHex] = false;
    this.#localRoomListenersAttached[idHex] = false;
    this.#stoppingLocalMap[idHex] = false;
    this.#liveKitRoomsLocal[idHex] = new LiveKitRoom({
      adaptiveStream: false,
      reconnectPolicy: localReconnectPolicy
    });

    // Keep raw teardown serialized even when its logical recovery waiter has
    // timed out. No state writes occur after this captured Room's await.
    const raw = this.#disconnectLocalRoom(liveKitRoom);
    const previous = this.#localOpQueues[idHex] ?? Promise.resolve();
    this.#localOpQueues[idHex] = Promise.allSettled([previous, raw]).then(() => undefined);
    return raw;
  }

  public async restartCloud(): Promise<void> {
    await this.#rebuildCloud();
  }

  /**
   * Kick an immediate rebuild of every room that is not Connected. Connected
   * rooms stay with the SDK reconnect policy.
   *
   * Local rooms reuse the SAME recovery machinery as RoomEvent.Disconnected
   * (arm #localModeRecovery + paced loop, then one guarded
   * #rebuildLocalAfterDisconnect attempt) so failure counting and the
   * LOCAL_VIDEO_RETRY_LIMIT cloud failover keep working after a wake; #1658's
   * separate stopLocal+startLocal backoff loop was folded into this path.
   */
  public onSystemResume(_payload?: { sleepMs?: number | null }): void {
    if (this.#stopped) return;

    if (this.#liveKitRoomCloud && this.#liveKitRoomCloud.state !== ConnectionState.Connected) {
      void this.#rebuildCloud();
    }

    // Local rooms are Electron-only; on web they are constructed but never
    // started, so a resume must not spin a startLocal failure loop.
    if (!isElectronApp()) return;

    for (const localEncoderId of this.#localEncoderIds) {
      const idHex = toHexFromId(localEncoderId)!;
      const local = this.#liveKitRoomsLocal[idHex];
      // Only rooms this instance started: a never-started room has no
      // token/URI to recover and startLocal would just fail-count it.
      if (!local || !this.#startedLocalMap[idHex]) {
        continue;
      }
      // Wake recovery also checks the publisher connection: signaling can remain Connected
      // after media transport dies.
      if (local.state === ConnectionState.Connected && !publisherPcManagerClosed(local)) {
        continue;
      }
      if (this.#stoppingLocalMap[idHex]) continue;
      void this.restartLocalConnection(localEncoderId);
    }
  }

  // Race the delay against stop() so a teardown does not leave a backoff timer
  // running. Resolves on abort so the caller can re-check #stopped.
  // Per rule: settle-every-terminal-branch (ai.knowledge/global) — stop is a terminal path.
  async #sleepAbortable(ms: number): Promise<void> {
    const signal = this.#stopController.signal;
    if (signal.aborted) return;

    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = (): void => {
        clearTimeout(timer);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  }

  async #rebuildCloud(): Promise<void> {
    if (this.#stopped || this.#rebuildingCloud || !this.#liveKitRoomCloud) return;
    this.#rebuildingCloud = true;
    try {
      for (let attempt = 0; !this.#stopped; attempt++) {
        try {
          const { token, endpoint } = await this.#mintCloudToken();
          // Recheck after every await: leave-during-mint must not connect.
          // Per rule: settle-every-terminal-branch (ai.knowledge/global) — stop is a terminal path.
          if (this.#stopped) return;
          this.#token = token;
          this.#endpoint = endpoint;
          await this.#liveKitRoomCloud.connect(endpoint, token, { autoSubscribe: false });
          // A connect that completes after stop() still has to be torn down.
          if (this.#stopped) {
            await this.stopCloud();
            return;
          }
          for (const agent of broker) {
            if (this.#stopped) {
              await this.stopCloud();
              return;
            }
            void this.refreshTrackSubscriptions(toIdFromHex(agent.id)!);
          }
          return;
        } catch (error) {
          if (this.#stopped) return;
          logger.warning('cloud room rebuild failed', { attempt, error });
          await this.#sleepAbortable(Math.min(30_000, 1_000 * 2 ** attempt));
        }
      }
    } finally {
      // Per rule: settle-every-terminal-branch (ai.knowledge/global) — clear the
      // single-flight flag on success, stop, and every thrown path.
      this.#rebuildingCloud = false;
    }
  }

  public async attemptStartLocal(agentId: Uint8Array): Promise<void> {
    try {
      await this.startLocal(agentId);
    } catch (error) {
      throw error;
    }
  }

  /** Stops the LiveKit track synchronization. */
  public async stop(): Promise<void> {
    let stopError: unknown;
    this.#stopped = true;
    this.#stopController.abort();
    for (const idHex of Object.keys(this.#localRecoveryTimers)) {
      clearTimeout(this.#localRecoveryTimers[idHex]);
      this.#localRecoveryTimers[idHex] = undefined;
    }
    // Per settle-every-terminal-branch: one stalled SDK cleanup cannot keep
    // another agent's mint/probe/connect or pre-connect recovery wait alive.
    const localStops = this.#localEncoderIds.map((agentId) => this.stopLocal(agentId));
    // The 30s cancelation revert would otherwise setMode from a dead instance.
    clearTimeout(this.modeCancelationTimeout);

    try {
      if (this.#liveKitRoomCloud) {
        try {
          await this.stopCloud();
        } catch (error) {
          stopError = error;
        }
      }

      for (const localStop of localStops) {
        try {
          await localStop;
        } catch (error) {
          stopError ??= error;
        }
      }
    } finally {
      for (const unsubscribe of this.#userActivityUnsubscribers.splice(0)) unsubscribe();
      this.#clearGovernorResources();
    }

    if (stopError) throw stopError;
  }

  public async runConnectionDiagnostics(): Promise<LiveKitConnectionDiagnosticSummary> {
    // ENG-4223 / ENG-4174 / ENG-4247: manual diagnostics run while the room is
    // CONNECTED, so the probe's ConnectionCheck (a genuine SFU join) MUST carry
    // the distinct diagnostic identity. Joining with #token's identity makes the
    // SFU evict the live participant (DisconnectReason.DUPLICATE_IDENTITY, a
    // terminal disconnect that bypasses reconnectPolicy) — the webapp
    // "bridge offline" + black-feed wedge. No diagnostic token → SKIP entirely;
    // NEVER fall back to #token.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    if (!this.#diagnosticToken) {
      logger.warning(
        `LiveKit manual connection diagnostics skipped for room ${this.id}: ` +
          'no diagnostic token minted (running with the live token would evict the active participant).'
      );
      return {
        runId: `lkdiag-manual-skipped-${Date.now().toString(36)}`,
        status: 'skipped',
        results: []
      };
    }

    return runLiveKitConnectionDiagnostics({
      url: this.#endpoint,
      token: this.#diagnosticToken,
      mode: 'cloud',
      trigger: 'manual',
      roomId: this.id,
      throttleMs: 0
    });
  }

  #runConnectionDiagnostics(
    mode: 'cloud' | 'local',
    trigger: 'cloud-connect-failed' | 'local-connect-failed',
    override: { url: string; token: string; agentId?: string } | undefined,
    cause: unknown
  ): void {
    const url = override?.url ?? this.#endpoint;
    // ENG-4223: connect-failed probes launch ASYNC while the app may retry the
    // real connect. If the probe joins with #token's identity AFTER a retry has
    // reconnected, the SFU evicts the freshly connected live participant
    // (DUPLICATE_IDENTITY) — the same eviction class the startup probe fixed in
    // ENG-4174/ENG-4247. Prefer the distinct-identity diagnostic token whenever
    // it was minted (always on web/cloud). The live-token fallback only remains
    // for Electron, where the diagnostic token is intentionally never minted
    // and this failure path predates ENG-4223 (ticket scope: webapp-only).
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    const token =
      override?.token ??
      (mode === 'cloud' && this.#diagnosticToken ? this.#diagnosticToken : this.#token);
    const agentId = override?.agentId;

    // Failure diagnostics run out-of-band so a support probe never blocks UI recovery.
    runLiveKitConnectionDiagnostics({
      url,
      token,
      mode,
      trigger,
      roomId: this.id,
      ...(agentId ? { agentId } : {}),
      cause
    }).catch((diagnosticError) => {
      logger.warning('LiveKit connection diagnostics failed to run.', diagnosticError);
    });
  }

  startLocalPromises: Record<string, Promise<void> | undefined> = {};
  startCloudPromise: Promise<void> | undefined;

  starting = false;

  public async start(
    unsafeWaitForLocal = false,
    agentIds: T_Maybe<Uint8Array[]> = undefined
  ): Promise<void> {
    if (this.#stopped) return;

    if (this.starting) {
      logger.warning('Skipping start because already starting.');
      return;
    }

    this.starting = true;

    const bridgeEncoderID = isElectronApp() ? await resolveLinkedAgentId() : undefined;
    if (this.#stopped) {
      this.starting = false;
      return;
    }

    const hasLocalEncoderID = isElectronApp()
      ? agentIds?.some((id) => toHexFromId(id) === bridgeEncoderID)
      : false;

    let noCloud = hasLocalEncoderID;

    if (!this.startCloudPromise) {
      this.startCloudPromise = this.startCloud();
    }

    if (noCloud) {
      setTimeout(async () => {
        try {
          await this.startCloudPromise;
        } catch (error) {
          console.error('Failed to start cloud', error);
        } finally {
          this.startCloudPromise = undefined;
        }
      });
    } else {
      try {
        await this.startCloudPromise;
      } catch (error) {
        console.error('Failed to start cloud', error);
      } finally {
        for (const agentId of agentIds ?? []) {
          const idHex = toHexFromId(agentId)!;
          this.#mode[idHex] = resolveStartCloudMode(this.#mode[idHex], isElectronApp());
          this.refreshTrackSubscriptions(agentId);
        }

        this.startCloudPromise = undefined;
      }
    }

    if (this.#stopped) {
      this.starting = false;
      return;
    }

    const waitForLocal = (unsafeWaitForLocal ?? false) || noCloud;

    for (const localEncoderId of agentIds ?? this.#localEncoderIds) {
      this.#ensureModeEntry(localEncoderId);
      const idHex = toHexFromId(localEncoderId)!;

      if (!this.startLocalPromises[idHex]) {
        const promise = (this.startLocalPromises[idHex] = waitForLocal
          ? this.attemptStartLocal(localEncoderId)
          : this.startLocal(localEncoderId));

        void promise
          .finally(() => {
            delete this.startLocalPromises[idHex];
          })
          .catch((error) => {
            if (!waitForLocal) logger.error('Detached local startup failed.', error);
          });
      }
    }

    try {
      if (waitForLocal) {
        await Promise.all(Object.values(this.startLocalPromises));
      }
    } catch (error) {
      console.error('Failed to start local', error);
      throw error;
    } finally {
      this.starting = false;
    }

    if (this.#stopped) return;

    window.addEventListener('visibilitychange', this.#focusListener);

    this.starting = false;
  }

  public modeForAgent(id: Uint8Array): 'local' | 'cloud' {
    // Embedded sources have no local transport: report cloud even when #mode was
    // seeded from the Electron 'local' preference, matching #advertisedSessionMode.
    if (this.#isEmbeddedAgent(id)) return 'cloud';
    return this.#mode[toHexFromId(id)!] ?? 'cloud';
  }

  #ensureModeEntry(agentId: Uint8Array): void {
    const idHex = toHexFromId(agentId)!;
    if (this.#mode[idHex] === undefined) {
      this.#mode[idHex] = this.#preferenceMode;
    }
  }

  #seedModeEntriesFromConnectedAgents(): void {
    for (const id of this.#localEncoderIds) {
      this.#ensureModeEntry(id);
    }
    for (const hex of this.presentAgents.keys()) {
      const id = toIdFromHex(hex);
      if (id) this.#ensureModeEntry(id);
    }
    for (const agent of broker) {
      const id = toIdFromHex(agent.id);
      if (id) this.#ensureModeEntry(id);
    }
  }

  // An embedded (iris-enabled) source has no local transport — its video only
  // travels over cloud. Reuses the per-agent Proxy.embedded flag (the same
  // predicate agent/hooks.ts useAtLeastOneAgentIsEmbedded reads) so the T7
  // local-mode deny never darkens embedded cameras. Per rule: stay-in-scope.
  #isEmbeddedAgent(agentId: Uint8Array): boolean {
    const wanted = (toHexFromId(agentId) ?? '').toLowerCase();
    for (const agent of broker) {
      if (!agent) continue;
      const hex =
        typeof agent.id === 'string'
          ? agent.id.toLowerCase()
          : (toHexFromId(agent.id as Uint8Array) ?? '').toLowerCase();
      if (hex === wanted) return !!agent.embedded;
    }
    return false;
  }

  // Q1: terminal failover (#videoFailedOver) is exempt. Never key on
  // isVideoReconnecting — that flag clears at terminal entry. Embedded sources
  // are always cloud-allowed: their video is cloud-only, so the T7 local-mode
  // deny must never gate their cloud subscription. Per rule: stay-in-scope.
  #cloudVideoAllowedForAgent(agentId: Uint8Array): boolean {
    return (
      this.#cloudVideoPolicy === 'allow' ||
      this.#isEmbeddedAgent(agentId) ||
      !!this.#videoFailedOver[toHexFromId(agentId)!]
    );
  }

  public setCloudVideoPolicy(policy: 'allow' | 'deny'): void {
    if (!isElectronApp()) {
      this.#cloudVideoPolicy = 'allow';
      return;
    }
    // User toggle / auto-engage is the only clearer. Do not early-return
    // before the clear when already deny — that left T4 failover sticky and
    // a later local hiccup re-advertised cloud (I1). Failover itself does
    // not call this setter. Per rule: stay-in-scope (ai.knowledge/global).
    if (policy === 'deny') {
      this.#videoFailedOver = {};
    }
    const already = this.#cloudVideoPolicy === policy;
    this.#cloudVideoPolicy = policy;
    if (already && policy !== 'deny') {
      return;
    }
    this.#seedModeEntriesFromConnectedAgents();
    void this.refreshTrackSubscriptionsForAll();
    void this.#refreshMetadata();
    // T7 deny stops encoder cloud publish; allow must kick reattach so
    // operator/governor cloud requests get a live track (CI #1509 HIL).
    if (policy === 'allow') {
      void this.forceVideoReattach();
    }
  }

  public isVideoFailedOverToCloud(agentId: Uint8Array): boolean {
    return !!this.#videoFailedOver[toHexFromId(agentId)!];
  }

  #lockedLocal: Record<string, boolean> = {};

  public setLockedLocal(agentId: string, locked: boolean): void {
    const newValue = this.#lockedLocal[agentId] !== locked;

    this.#lockedLocal[agentId] = locked;

    if (locked) {
      this.#releaseCloudDefault(agentId);
    }

    if (newValue) {
      this.#refreshMetadata();
    }
  }

  modeCancelationTimeout: NodeJS.Timeout | undefined = undefined;

  // pipeline.ts setConnectionMode is a no-op ("always local"). The real
  // encoder reopen on a CLOUD switch is the same mutate switch.tsx
  // setLockedState performs: control.encoder.connectionMode ->
  // CONNECTION_MODE_CLOUD_AND_LOCAL. Without it, operator/governor cloud stays
  // CONNECTION_MODE_LOCAL (cloud_bitrate=0, CI #1509 east 19e11a7f0e mode=cloud
  // track=false).
  //
  // A LOCAL switch writes NOTHING here. CONNECTION_MODE_LOCAL is the admin
  // padlock — switch.tsx setLockedState is its only writer and switch.tsx
  // derives isLocked from that field. The desktop auto-selects local at boot
  // and on every recovery/reconnect, so mutating LOCAL here self-engaged the
  // admin lock on every boot. Per rule: stay-in-scope (ai.knowledge/global).
  async #applyEncoderConnectionMode(
    agentId: Uint8Array,
    mode: 'local' | 'cloud',
    auto = false
  ): Promise<void> {
    // Only a cloud switch reopens the encoder; local transport never touches
    // the admin-lock field.
    if (mode !== 'cloud') {
      return;
    }
    const wanted = (toHexFromId(agentId) ?? '').toLowerCase();
    // ENG-4665 H2/M3: background setMode('cloud') (30s revert, video failover)
    // must not unlock an admin padlock. switch.tsx is the only writer of
    // CONNECTION_MODE_LOCAL; this path only writes CLOUD_AND_LOCAL.
    if (this.#lockedLocal[wanted] || this.#lockedLocal[toHexFromId(agentId)!]) {
      logger.info(`setMode encoder mutate skipped: admin padlock locked agent=${wanted}`);
      return;
    }
    const payload = {
      control: {
        encoder: {
          connectionMode: ConnectionMode.CONNECTION_MODE_CLOUD_AND_LOCAL
        }
      }
    };
    const hexOf = (agent: Proxy): string => {
      if (typeof agent.id === 'string') return agent.id.toLowerCase();
      return (toHexFromId(agent.id as Uint8Array) ?? '').toLowerCase();
    };
    let mutated = false;
    for (const agent of broker) {
      if (!agent) continue;
      if (hexOf(agent) !== wanted) continue;
      try {
        // Call Proxy.mutate directly — a narrower (set: unknown) helper
        // failed studio CI TS2345 (parameter contravariance). Per rule:
        // stay-in-scope. CI #1509 ee91823079 studio Build.
        await agent.mutate(payload, {}, false);
        mutated = true;
      } catch (error) {
        logger.error(`setMode encoder mutate failed agent=${wanted} mode=${mode}`, error);
      }
    }
    // Background recovery owns only the affected agent. A missing or rejected
    // target must never fall through to mutation of healthy neighboring agents.
    if (auto) return;
    // Cloud unlock is additive (same as the Local Mode switch mutating every
    // agent). Id-format mismatch on east HIL swallowed the single-agent
    // mutate; CLOUD_AND_LOCAL on extra agents still lets local WebRTC run.
    // Per rule: stay-in-scope.
    if (!mutated) {
      for (const agent of broker) {
        if (!agent) continue;
        try {
          await agent.mutate(payload, {}, false);
          mutated = true;
        } catch (error) {
          logger.error(`setMode encoder mutate-all failed mode=${mode}`, error);
        }
      }
    }
    if (!mutated) {
      logger.info(`setMode encoder mutate skipped agent=${wanted} mode=${mode}`);
    }
  }

  public async setMode(
    mode: 'local' | 'cloud',
    agentId: Uint8Array,
    _autoPause: boolean = true,
    auto: boolean = false
  ): Promise<void> {
    const idHexAtRequest = toHexFromId(agentId)!;
    const roomAtRequest = this.#liveKitRoomsLocal[idHexAtRequest];
    // Re-checked after every await: an automatic local switch also yields to any Cloud hold that
    // appears mid-flight (operator request or the ENG-4730 embedded-studio Cloud default).
    const current = (): boolean =>
      !this.#stopped &&
      (!auto || roomAtRequest === this.#liveKitRoomsLocal[idHexAtRequest]) &&
      !(auto && mode === 'local' && this.#holdsCloud(idHexAtRequest));
    if (!current()) return;
    // Auto-local must not clobber a fresher operator cloud request: the local
    // room's Connected+1s timer and the one-shot auto-switchover both fire
    // setMode('local') unconditionally, and post-reload that timer landed
    // ~500ms AFTER the operator's setMode('cloud') and won — then deleted the
    // #operatorRequestedCloud latch, erasing the operator's intent entirely
    // (CI #1509 run 32904567960: cloud @22:18:35.10, auto-local @35.65/35.78,
    // device stuck local for the rest of the test). Auto callers now defer to
    // the latch; only operator-initiated local mode clears it.
    if (mode === 'local' && auto) {
      const idHexGuard = toHexFromId(agentId)!;
      if (this.#operatorRequestedCloud.has(idHexGuard)) {
        logger.info(`setMode auto-local skipped: operator requested cloud agent=${idHexGuard}`);
        return;
      }
      if (this.#holdsCloud(idHexGuard)) {
        logger.info(
          `setMode auto-local skipped: embedded studio cloud default agent=${idHexGuard}`
        );
        return;
      }
    }
    this.#preferenceMode = mode;
    const prevConnectionMode = this.connectedModeForAgent(agentId);
    const idHexForLog = toHexFromId(agentId)!;
    logger.info(`setMode mode=${mode} agent=${idHexForLog} prev=${prevConnectionMode}`);

    clearTimeout(this.modeCancelationTimeout);

    try {
      const idHex = toHexFromId(agentId)!;

      // Operator cloud request must unhide #videoCloud immediately, even when
      // #mode is already cloud. Iris cameras skip setMode('local') so auto-engage
      // leaves leftover prestart; the early-return below used to skip the
      // delete and starve governor HIL of a live track. After the 15s hide
      // expired, no refresh ran. Per rule: stay-in-scope. CI #1509 4d4775678f.
      if (mode === 'cloud') {
        this.#localModePrestartAt.delete(idHex);
        // The latch records OPERATOR intent — auto cloud (e.g. the 30s
        // cancelation revert) must not claim it, or auto-local would be
        // locked out forever afterwards.
        if (!auto) this.#operatorRequestedCloud.add(idHex);
      } else if (!auto) {
        // Only an operator-initiated local switch withdraws the cloud
        // request; auto callers were already gated on the latch above.
        this.#operatorRequestedCloud.delete(idHex);
      }
      // The operator's own choice (either mode) supersedes the embedded-studio default.
      if (!auto) this.#cloudByDefault.delete(idHex.toLowerCase());

      // Encoder weld VideoMode must flip before subscribe. pipeline
      // setConnectionMode is a stub; this mutate is the switch.tsx path.
      await this.#applyEncoderConnectionMode(agentId, mode, auto);
      if (!current()) return;

      if (this.#mode[idHex] === mode) {
        if (mode === 'cloud') {
          // T7 session deny unpublishes encoder cloud tracks. Client-side
          // refresh alone leaves snapshot.track=false (CI #1509 53ad25f8a2
          // east-lab: mode=cloud track=false after 6727541a12 prestart unhide).
          await setConnectionMode('cloud');
          if (!current()) return;
          await this.refreshTrackSubscriptions(agentId);
          if (!current()) return;
          await this.#refreshMetadata();
          if (!current()) return;
          // CI #1509 51b758/1c7240 east: encoder=3 track=false.
          // refreshTrackSubscriptions keys locateProxy(identity) as a device
          // id so agent-identity pubs skip. Subscribe every allowed cloud
          // video pub. Per rule: stay-in-scope.
          this.#subscribeAllAllowedCloudVideo(agentId);
          await this.forceVideoReattach();
        }
        return;
      }

      this.#mode[idHex] = mode;

      await setConnectionMode(this.#mode[idHex]!);
      if (!current()) return;

      this.modeCancelationTimeout = setTimeout(() => {
        if (!current()) return;
        if (this.connectedModeForAgent(agentId) !== mode && this.#mode[idHex] === mode) {
          // auto=true: the revert is machinery, not operator intent — it must
          // neither claim the cloud latch nor override a standing one.
          this.setMode(prevConnectionMode, agentId, false, true);
        }
      }, 30_000);
    } catch (error) {
      if (!current()) return;
      const idHex = toHexFromId(agentId)!;
      this.#mode[idHex] = prevConnectionMode;

      console.error('Failed to set mode', error);

      throw error;
    }

    if (!current()) return;
    this.refreshTrackSubscriptions(agentId);
    this.#refreshMetadata();
    // 1c7240 east :184: setMode cloud from prev=local took THIS path,
    // not already-cloud. Last: mode=cloud encoder=3 track=false because
    // refreshTrackSubscriptions skipped agent-identity pubs. Same
    // subscribe-all as already-cloud. Per rule: stay-in-scope.
    if (mode === 'cloud') {
      this.#subscribeAllAllowedCloudVideo(agentId);
    }

    setTimeout(() => {
      if (!current()) return;
      // Encoder cloud pubs often appear after the mutate, not at setMode.
      // Re-run subscribe-all before reattach so videoForAgent can bind.
      // Per rule: stay-in-scope.
      if (mode === 'cloud') {
        this.#subscribeAllAllowedCloudVideo(agentId);
      }
      this.forceVideoReattach();
    }, 1000);
  }

  /** Apply codec rejection to every subscription entry point, including delayed stall recovery. */
  #canSubscribeVideo(
    room: LiveKitRoom,
    participant: RemoteParticipant,
    publication: RemoteTrackPublication
  ): boolean {
    return this.#videoSubscriptionFailures.canSubscribe(
      room,
      participant,
      publication,
      () =>
        !this.#stopped &&
        (room === this.#liveKitRoomCloud || Object.values(this.#liveKitRoomsLocal).includes(room))
    );
  }

  public getVideoSubscriptionFailures(
    deviceId: string
  ): Array<{ codec: string; error: 'codec-unsupported' }> {
    return this.#videoSubscriptionFailures.diagnostic(deviceId);
  }

  public async refreshTrackSubscriptionsForAll(): Promise<void> {
    for (const agentId of this.#localEncoderIds) {
      await this.refreshTrackSubscriptions(agentId);
    }
  }

  localTrackSubscriptionMap: Record<Device['id'], boolean> = {};
  cloudTrackSubscriptionMap: Record<Device['id'], boolean> = {};

  watchingDeviceIDs: Record<string, NodeJS.Timeout> = {};

  public async watchForDeviceIDOnLocal(
    _deviceId: string,
    _agentId: Uint8Array,
    _timeout: number = 20_000
  ): Promise<void> {}

  // Operator/HIL setMode('cloud') with session allow. refreshTrackSubscriptions
  // gates on locateProxy(participant.identity as device id) and skips east-lab
  // agent-identity pubs (CI #1509 1c7240 :184 prev=local, encoder=3, track=false).
  // Subscribe every cloud video publication when policy allows. Per rule:
  // stay-in-scope (ai.knowledge/global).
  #subscribeAllAllowedCloudVideo(agentId: Uint8Array): void {
    if (!this.#cloudVideoAllowedForAgent(agentId)) return;
    // Read through modeForAgent, NOT the raw #mode map. An unseeded agent is
    // `undefined` in #mode while modeForAgent() — every other reader in this
    // file — reports its 'cloud' default, so a raw read here is the one place
    // that could disagree with the rest of the class.
    //
    // Honest scope: this is a consistency fix, NOT a live bug fix. All three
    // call sites reach this method with #mode already seeded, so the two reads
    // return the same value today — refreshTrackSubscriptions() runs
    // #ensureModeEntry(agentId) as its first statement (synchronously, before
    // its first await) and both the TrackPublished and ParticipantConnected
    // sites call it immediately before this one; the setMode 1s reattach site
    // is after setMode wrote the entry itself. The raw read is still wrong to
    // leave here: it silently depends on that ordering, and any future caller
    // that skips the refresh would get 'bail' where modeForAgent says 'cloud'.
    // Per rule: stay-in-scope (ai.knowledge/global).
    if (this.modeForAgent(agentId) !== 'cloud') return;
    this.#liveKitRoomCloud?.remoteParticipants.forEach((participant) => {
      try {
        participant.videoTrackPublications.forEach((pub) => {
          if (
            !pub.isSubscribed &&
            !this.#stalePublications.has(pub) &&
            this.#canSubscribeVideo(this.#liveKitRoomCloud!, participant, pub) &&
            !isTrialCameraLockedDevice(this.id, participant.identity)
          )
            pub.setSubscribed(true);
        });
      } catch (error) {
        logger.error('setMode cloud subscribe failed', error);
      }
    });
  }

  /**
   * @private embedded link recovery: cloud-subscribe a participant's published tracks directly
   * when the client's device→agent map doesn't yet know it (a just-linked camera,
   * state-sync lag) — refreshTrackSubscriptions' locateProxy gate would skip it,
   * and autoSubscribe is off, so the track would otherwise never subscribe and the
   * source stays "Connecting…" until a reload. Tray / video-blocked clients don't
   * cloud-subscribe; once the map catches up, refreshTrackSubscriptions reconciles
   * to the correct local/cloud value. Per rule: reference-rules-in-comments.
   */
  #subscribeUnmappedCloudTracks(participant: RemoteParticipant): void {
    // Hard deny: unmapped participants have no agent, so no failover exemption.
    if (isElectronApp('tray') || this.videoBlocked || this.#cloudVideoPolicy === 'deny') {
      return;
    }

    participant.trackPublications.forEach((pub) => {
      if (
        !pub.isSubscribed &&
        !this.#stalePublications.has(pub) &&
        this.#canSubscribeVideo(this.#liveKitRoomCloud!, participant, pub) &&
        !isTrialCameraLockedDevice(this.id, participant.identity)
      ) {
        pub.setSubscribed(true);
      }
    });
  }

  /**
   * Routing-only: sets track subscription state for a single agent's remote
   * participants in both the cloud room and any local room.
   *
   * For each remote participant, subscribes to cloud tracks when the connection
   * mode is "cloud" (or unresolved), and to local tracks when the mode is "local".
   * If `videoBlocked` is true, all video/audio subscriptions are disabled
   * regardless of mode (governor policy enforcement point for blocking actions).
   *
   * Does NOT manage quality caps, simulcast layer selection, or governor FSM
   * state — those are handled by `#runGovernorTick()`. This function only
   * decides which room's tracks to subscribe to.
   *
   * Called when: connection mode changes, track selection changes, or
   * `videoBlocked` state changes (e.g. governor pause action).
   */
  public async refreshTrackSubscriptions(agentId: Uint8Array): Promise<void> {
    this.#ensureModeEntry(agentId);
    const cloudVideoAllowed = this.#cloudVideoAllowedForAgent(agentId);
    this.#liveKitRoomCloud?.remoteParticipants.forEach((participant) => {
      participant.videoTrackPublications.forEach((pub) => {
        const deviceID = (participant.identity ?? '') as Device['id'];
        // Existing subscriptions may serve production already in progress.
        // Refuse only new subscriptions; UI previews independently hide locks.
        if (!pub.isSubscribed && isTrialCameraLockedDevice(this.id, participant.identity)) return;
        const proxy = locateProxy(deviceID);

        if (proxy?.id.toLowerCase() !== toHexFromId(agentId)!.toLowerCase()) {
          return;
        }

        const isLocal = this.videoBlocked
          ? false
          : this.connectedModeForAgent(toIdFromHex(proxy.id)!) === 'local' &&
            this.#mode[toHexFromId(agentId)!] === 'local';
        const cloudSubscribed =
          this.videoBlocked || this.#stalePublications.has(pub)
            ? false
            : isElectronApp('tray')
              ? false
              : !cloudVideoAllowed
                ? false
                : !isLocal && this.#canSubscribeVideo(this.#liveKitRoomCloud!, participant, pub);

        if (pub.isSubscribed === cloudSubscribed) {
          return;
        }

        this.cloudTrackSubscriptionMap[deviceID] = cloudSubscribed;
        pub.setSubscribed(cloudSubscribed);
      });

      participant.audioTrackPublications.forEach((pub) => {
        const deviceID = (participant.identity ?? '') as Device['id'];
        if (!pub.isSubscribed && isTrialCameraLockedDevice(this.id, participant.identity)) return;
        const proxy = locateProxy(deviceID);

        if (proxy?.id.toLowerCase() !== toHexFromId(agentId)!.toLowerCase()) {
          return;
        }

        const isLocal = this.videoBlocked
          ? false
          : this.connectedModeForAgent(toIdFromHex(proxy.id)!) === 'local' &&
            this.#mode[toHexFromId(agentId)!] === 'local';
        const cloudSubscribed =
          this.videoBlocked || this.#stalePublications.has(pub)
            ? false
            : isElectronApp('tray')
              ? false
              : !cloudVideoAllowed
                ? false
                : !isLocal;

        if (pub.isSubscribed === cloudSubscribed) {
          return;
        }

        this.cloudTrackSubscriptionMap[deviceID] = cloudSubscribed;
        pub.setSubscribed(cloudSubscribed);
      });
    });

    this.#liveKitRoomsLocal[toHexFromId(agentId)!]?.remoteParticipants.forEach((participant) => {
      participant.videoTrackPublications.forEach((pub) => {
        const deviceID = (participant.identity ?? '') as Device['id'];
        if (!pub.isSubscribed && isTrialCameraLockedDevice(this.id, participant.identity)) return;
        const proxy = locateProxy(deviceID);

        if (proxy?.id.toLowerCase() !== toHexFromId(agentId)!.toLowerCase()) {
          return;
        }

        const isLocal = this.videoBlocked
          ? false
          : this.connectedModeForAgent(toIdFromHex(proxy.id)!) === 'local' &&
            this.#mode[toHexFromId(agentId)!] === 'local';
        const localSubscribed =
          isLocal &&
          !this.#stalePublications.has(pub) &&
          this.#canSubscribeVideo(
            this.#liveKitRoomsLocal[toHexFromId(agentId)!]!,
            participant,
            pub
          );

        if (pub.isSubscribed === localSubscribed) {
          return;
        }

        this.localTrackSubscriptionMap[deviceID] = localSubscribed;
        pub.setSubscribed(localSubscribed);

        // @ts-ignore
        setTimeout(() => {
          // @ts-ignore
          if (window.worker?.deviceIDHex === deviceID) {
            // @ts-ignore
            window.worker.refreshTrack();
          }
        }, 1_000);
      });

      participant.audioTrackPublications.forEach((pub) => {
        const deviceID = (participant.identity ?? '') as Device['id'];
        if (!pub.isSubscribed && isTrialCameraLockedDevice(this.id, participant.identity)) return;
        const proxy = locateProxy(deviceID);

        if (proxy?.id.toLowerCase() !== toHexFromId(agentId)!.toLowerCase()) {
          return;
        }

        const isLocal = this.videoBlocked
          ? false
          : this.connectedModeForAgent(toIdFromHex(proxy.id)!) === 'local' &&
            this.#mode[toHexFromId(agentId)!] === 'local';
        const localSubscribed = isLocal && !this.#stalePublications.has(pub);

        if (pub.isSubscribed === localSubscribed) {
          return;
        }

        this.localTrackSubscriptionMap[deviceID] = localSubscribed;
        pub.setSubscribed(localSubscribed);
      });
    });
  }

  public async publishDataWithLocalParticipant(
    agentId: Uint8Array,
    participantOptions: {
      mode?: 'local' | 'cloud' | 'hybrid' | 'auto';
    },
    data: Uint8Array,
    options?: DataPublishOptions
  ): Promise<void> {
    const agentIdHex = toHexFromId(agentId)!;

    // The SDK has directly retired this leg's publisher PC. Recovery is
    // requested from the affected send path, never inferred from peer traffic.
    if (
      this.#liveKitRoomsLocal[agentIdHex]?.state === ConnectionState.Connected &&
      publisherPcManagerClosed(this.#liveKitRoomsLocal[agentIdHex])
    ) {
      void this.restartLocalConnection(agentId);
    }
    const localConnected =
      this.#liveKitRoomsLocal[agentIdHex]?.state === ConnectionState.Connected &&
      !publisherPcManagerClosed(this.#liveKitRoomsLocal[agentIdHex]);
    const cloudConnected =
      this.#liveKitRoomCloud?.state === ConnectionState.Connected &&
      !publisherPcManagerClosed(this.#liveKitRoomCloud);

    let isLocal = participantOptions?.mode === 'local' && localConnected;
    let isCloud = participantOptions?.mode === 'cloud' && cloudConnected;

    if (
      participantOptions.mode === 'hybrid' ||
      participantOptions.mode === 'auto' ||
      participantOptions.mode === undefined
    ) {
      // S5: publish every connected leg. #mode is media routing only;
      // state dual-publishes. Skip a not-connected room so we don't throw
      // UnexpectedConnectionState / busy-wait peerConnectionTimeout.
      isLocal = localConnected;
      isCloud = cloudConnected;
    }

    if (!isLocal && !isCloud) {
      throw new Error('publishData skipped: no connected LiveKit room');
    }

    // ENG-4665 H2: ConnectionState.Connected is signal-plane only. A room
    // whose publisher PC is gone still reports Connected and then hangs inside
    // publishData (~peerConnectionTimeout). Skip a dead leg when the other is
    // up; never skip the last remaining connected leg. Mocks without `engine`
    // (local-dual-state FakeRoom) are treated as healthy.
    const localFails = this.#consecutiveSendFailureMapLocal[agentIdHex] ?? 0;
    const cloudFails = this.#consecutiveSendFailureMapCloud[agentIdHex] ?? 0;
    const localPcClosed = publisherPcManagerClosed(this.#liveKitRoomsLocal[agentIdHex]);
    const cloudPcClosed = publisherPcManagerClosed(this.#liveKitRoomCloud);

    if (isLocal && isCloud) {
      if (localFails >= PUBLISH_LEG_CIRCUIT_OPEN || localPcClosed) {
        logger.warning(
          `publishData skip local leg agent=${agentIdHex} fails=${localFails} pcClosed=${localPcClosed}`
        );
        isLocal = false;
      } else if (cloudFails >= PUBLISH_LEG_CIRCUIT_OPEN || cloudPcClosed) {
        logger.warning(
          `publishData skip cloud leg agent=${agentIdHex} fails=${cloudFails} pcClosed=${cloudPcClosed}`
        );
        isCloud = false;
      }
    }

    let localPromise: Promise<void> | undefined;
    let cloudPromise: Promise<void> | undefined;

    if (isLocal) {
      const localParticipant = this.localParticipantWithOptions(agentId, { mode: 'local' });
      if (localParticipant) {
        localPromise = localParticipant.publishData(data, options);
      }
    }

    if (isCloud) {
      const cloudParticipant = this.localParticipantWithOptions(agentId, { mode: 'cloud' });
      if (cloudParticipant) {
        cloudPromise = cloudParticipant.publishData(data, options);
      }
    }

    const settleLeg = async (
      promise: Promise<void> | undefined,
      label: 'local' | 'cloud',
      failMap: Record<string, number>
    ): Promise<Error | undefined> => {
      if (!promise) {
        return undefined;
      }
      try {
        await awaitPublishLeg(promise, label);
        failMap[agentIdHex] = 0;
        return undefined;
      } catch (error) {
        const err = error as Error;
        failMap[agentIdHex] = (failMap[agentIdHex] ?? 0) + 1;
        logger.warning(`publishData ${label} failed agent=${agentIdHex}: ${err.message}`);
        return err;
      }
    };

    // Await both legs in parallel so a hung local cannot stall a live cloud
    // (and vice versa). Each await is itself bounded by PUBLISH_LEG_TIMEOUT_MS.
    const [localError, cloudError] = await Promise.all([
      settleLeg(localPromise, 'local', this.#consecutiveSendFailureMapLocal),
      settleLeg(cloudPromise, 'cloud', this.#consecutiveSendFailureMapCloud)
    ]);

    if (
      participantOptions.mode === 'hybrid' ||
      participantOptions.mode === 'auto' ||
      participantOptions.mode === undefined
    ) {
      // Hybrid dual-sends the same one-shot command over both routes so a
      // local-route replacement cannot silently drop it; the device and Studio
      // message-id guards dedupe whichever copy arrives second. Succeed if EITHER
      // attempted leg delivered — throwing when the other leg also delivered would
      // make Studio roll back optimistic UI for a command the agent already ran.
      // Only fail when every attempted leg errored, and name both causes so the
      // failure is diagnosable instead of surfacing just one side.
      const localDelivered = localPromise !== undefined && localError === undefined;
      const cloudDelivered = cloudPromise !== undefined && cloudError === undefined;

      if (!localDelivered && !cloudDelivered) {
        const causes = [
          localError !== undefined ? `local: ${localError.message}` : undefined,
          cloudError !== undefined ? `cloud: ${cloudError.message}` : undefined
        ]
          .filter((cause) => cause !== undefined)
          .join('; ');

        throw new Error(`hybrid publishData failed on all attempted legs (${causes})`);
      }

      return;
    }

    if (cloudError) {
      throw cloudError;
    }

    if (localError) {
      throw localError;
    }
  }

  // Per-agent advertised session mode for cloud metadata (the T7 clamp).
  // Embedded (iris-enabled) sources are cloud-only (no local transport), so they
  // ALWAYS advertise cloud — the camera-side publish gate (lk sessionRequestsCloud)
  // keeps cloud send open and the local-mode deny/clamp never darkens them. Every
  // other source follows the clamp order: force-local (boot/tray/blocked), then
  // the cloud-video-policy deny, then an explicit request, then the started-local
  // fallback. Per rule: stay-in-scope (ai.knowledge/global).
  #advertisedSessionMode(
    agentId: Uint8Array,
    requested: 'local' | 'cloud',
    forceLocal: boolean
  ): 'local' | 'cloud' {
    if (this.#isEmbeddedAgent(agentId)) {
      return 'cloud';
    }
    if (forceLocal || this.isStartingLocalMode(agentId)) {
      return 'local';
    }
    if (!this.#cloudVideoAllowedForAgent(agentId)) {
      return 'local';
    }
    // Operator/governor cloud request must advertise cloud so the encoder
    // reopens cloud send. switch.tsx setLockedLocal from encoder
    // CONNECTION_MODE_LOCAL otherwise deadlocks metadata on local (CI #1509
    // 53ad25f8a2: mode=cloud track=false, encoder cloud_bitrate=0). Deny still
    // clamps above. Per rule: stay-in-scope.
    if (requested === 'cloud') {
      return 'cloud';
    }
    // Symmetric to the branch above: an explicit LOCAL request must advertise
    // local too. Without this a freshly-added second camera advertises cloud
    // purely because its local video has not started yet --
    // unsafeConnectedModeForAgent only honors resolvedMode 'local' when
    // #startedLocalMap is set, so a seeded agent falls through to its terminal
    // `return 'cloud'` and outranks the request nobody made. CI #1509 run
    // 32699244214 mac Phase B: two cameras, {8ff0dea6:local, 9f0ea077:cloud},
    // and setMode was never once called for 9f0ea077.
    // GATED ON localModeCapable, not on `requested` alone, because
    // #preferenceMode is 'local' for every Electron session -- an unconditional
    // local branch would also clamp a genuine cloud session that simply never
    // started local video, which is the case the 'allow leaves subscribe and
    // metadata fallback unchanged' test pins. Some agent having started local is
    // what distinguishes "this Electron is running local mode" from "this
    // Electron is a cloud session". Per rule: stay-in-scope.
    if (requested === 'local' && this.localModeCapable) {
      return 'local';
    }
    return this.unsafeConnectedModeForAgent(agentId, true);
  }

  #startedAt: Date | undefined;
  #lastRefreshedMetadata: Date | undefined;

  #refreshMetadata = async () => {
    if (this.#startedAt === undefined) {
      this.#startedAt = new Date();
    }

    const { user } = CurrentUser;

    const nameArr = [user?.firstName, user?.lastName].filter(Boolean);

    const isActive = getUserIsActive();

    const hybridVisibleOnline = isActive;

    // Force local if the app is running in the tray or if it has just started and it's local mode capable
    const forceLocal =
      this.videoBlocked ||
      isElectronApp('tray') ||
      (isElectronApp() && +new Date() - +this.#startedAt < 2_000);

    // Every connected agent (embedded + late-added) must have a #mode entry so
    // the clamp emits a session for it — 'local' under deny for a source with a
    // local path, 'cloud' for an embedded (cloud-only) source. Per Q2.
    this.#seedModeEntriesFromConnectedAgents();

    // Determine the session modes for each agent (T7 clamp + embedded
    // exemption live in #advertisedSessionMode).
    const sessions = Object.fromEntries(
      Object.entries(this.#mode).map(([id, requested]) => [
        id,
        { mode: this.#advertisedSessionMode(toIdFromHex(id)!, requested, forceLocal) }
      ])
    );

    const meta = {
      sessions,

      // Determine the overall mode for the session based on the individual agent sessions
      mode: Object.values(sessions).some((s) => s.mode === 'cloud') ? 'cloud' : 'local',

      identity: {
        userId: user?.id,
        userName: nameArr.join(' '),
        userPhoto: user?.profilePhotoUrl,
        sessionJoinedAt: +this.#startedAt!,
        isWorker: false,
        visible: hybridVisibleOnline,
        clientType: !hybridVisibleOnline
          ? ClientType.CLIENT_TYPE_GST_ADAPTER
          : isElectronApp('tray')
            ? ClientType.CLIENT_TYPE_ELECTRON_ENCODER
            : isElectronApp()
              ? ClientType.CLIENT_TYPE_ELECTRON
              : ClientType.CLIENT_TYPE_BROWSER
      }
    };

    this.#lastRefreshedMetadata = new Date();

    // Cloud metadata carries NO lastPing — the contract main ships. Embedded
    // camera firmware (lk/track.go participantTracker) drops a cloud
    // participant whose lastPing is more than 30s behind the CAMERA's wall
    // clock, and skips that gate only when lastPing is 0. Stamping it here
    // compared two devices' clocks and made every lab camera withhold cloud
    // video while the studio reported mode=cloud (bandwidth-governor HIL red
    // on every run carrying the stamp, green on main). Zombie-session ageing
    // must come from a contract old firmware honours; customers do not
    // update camera firmware. Local rooms keep lastPing: same machine, same
    // clock. Per rule: wire-contract-changes-must-not-break-old-firmware
    // (ai.knowledge/global).
    if (this.#liveKitRoomCloud?.state === ConnectionState.Connected) {
      this.#liveKitRoomCloud?.localParticipant.setMetadata(JSON.stringify(meta));
    }

    // @ts-ignore
    meta.lastPing = +(this.#lastRefreshedMetadata.getTime() / 1000);

    for (const id of this.#localEncoderIds) {
      const idHex = toHexFromId(id)!;

      const localRoom = this.#liveKitRoomsLocal[idHex]!;
      const localParticipant = localRoom.localParticipant;

      if (localRoom.state === ConnectionState.Connected) {
        localParticipant.setMetadata(JSON.stringify(meta));
      }
    }
  };

  /** @private */
  #handleTrackSubscribed =
    (mode: 'local' | 'cloud') =>
    async (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ): Promise<void> => {
      // An in-flight subscribe callback must not reinstall a retired track.
      if (
        this.#stalePublications.has(publication) ||
        this.#videoSubscriptionFailures.isRejected(publication)
      ) {
        publication.setSubscribed(false);
        return;
      }

      const { kind, sid = STR_NULL } = track;

      if (![Track.Kind.Audio, Track.Kind.Video].includes(kind)) {
        return;
      }

      const { identity } = participant;

      const bucket =
        mode === 'local'
          ? kind === Track.Kind.Audio
            ? this.#audioLocal
            : this.#videoLocal
          : kind === Track.Kind.Audio
            ? this.#audioCloud
            : this.#videoCloud;

      const deviceId = toDeviceId(identity);

      if (bucket.has(deviceId)) {
        const previous = bucket.get(deviceId)?.sid ?? STR_NULL;

        if (previous !== sid) {
          logger.warning(
            `Multiple ${kind} tracks detected for participant ${deviceId}.  Was previously subscribed to ${previous} but now subscribed to ${sid}.`
          );
        }
      }

      // embedded link recovery: bind by deviceId and emit immediately. With autoSubscribe:false a
      // just-linked embedded camera's track is subscribed here BEFORE the client's
      // device→agent map (#deviceIdLookup / locateProxy) resolves. Routing to the
      // correct agent is now handled on the READ side by trackBelongsToAgent
      // (videoForAgent/audioForAgent), so there is no need to poll #deviceIdLookup
      // for ~25s before binding — that old loop only delayed the source appearing
      // (or, pre-fix, silently dropped it → stuck "Connecting…" until reload). The
      // bucket is keyed by deviceId, the same key #synchronizeCloud and the
      // read-side filters use, so an emit('changed') now repaints the source the
      // moment its track arrives. Per rule: reference-rules-in-comments
      // (ai.knowledge/global) — preserve timeout and reconnect recovery paths.
      bucket.set(deviceId, track);

      // ENG-3716: this is the 'subscribed to video track' event the
      // waiting-for-video-track state machine is bound to — settle the wait so
      // the badge leaves "Connecting..." (and recovers any prior failure state).
      if (kind === Track.Kind.Video) {
        resolveVideoTrackWait(deviceId);
      }

      logger.debug(`Participant ${deviceId} subscribed to ${kind} track: ${sid}`);

      this.emit('changed').catch((error) => {
        logger.critical(error);
      });
    };

  /** @private */
  #handleTrackUnsubscribed =
    (mode: 'local' | 'cloud') =>
    ({ kind, sid = STR_NULL }: RemoteTrack, _: unknown, { identity }: RemoteParticipant): void => {
      if (![Track.Kind.Audio, Track.Kind.Video].includes(kind)) {
        return;
      }

      const deviceId = toDeviceId(identity);
      const bucket =
        mode === 'local'
          ? kind === Track.Kind.Audio
            ? this.#audioLocal
            : this.#videoLocal
          : kind === Track.Kind.Audio
            ? this.#audioCloud
            : this.#videoCloud;

      // Delete only the publication that emitted the event; a replacement may already be active.
      if (bucket.get(deviceId)?.sid === sid) bucket.delete(deviceId);

      logger.debug(`Participant ${deviceId} unsubscribed from ${kind} track: ${sid}`);

      this.emit('changed').catch((error) => {
        logger.critical(error);
      });
    };

  /** @private */
  #synchronizeCloud(): boolean {
    const selectTrack = (
      current: RemoteTrack | undefined,
      publications: Iterable<RemoteTrackPublication>,
      source: Track.Source
    ): RemoteTrack | undefined => {
      const candidates = [...publications].filter(
        (publication): publication is RemoteTrackPublication & { track: RemoteTrack } =>
          publication.track !== undefined &&
          !this.#stalePublications.has(publication) &&
          !this.#videoSubscriptionFailures.isRejected(publication)
      );

      // Preserve the event-selected replacement only when it matches the requested source.
      return (
        candidates.find(
          (publication) => publication.track === current && publication.source === source
        )?.track ??
        [...candidates].reverse().find((publication) => publication.source === source)?.track ??
        candidates[candidates.length - 1]?.track
      );
    };
    const updateTrack = (
      bucket: MutableTracks,
      deviceId: Device['id'],
      track: RemoteTrack | undefined
    ): boolean => {
      if (!track) return bucket.delete(deviceId);
      if (bucket.get(deviceId) === track) return false;
      bucket.set(deviceId, track);
      return true;
    };

    const activeDeviceIds = new Set<Device['id']>();
    let changed = false;

    // Process each of the [remote] participants and reconcile their current tracks.
    [...(this.#liveKitRoomCloud?.remoteParticipants.values() ?? [])].forEach((participant) => {
      const deviceId = toDeviceId(participant.identity);
      activeDeviceIds.add(deviceId);
      const audio = selectTrack(
        this.#audioCloud.get(deviceId),
        participant.audioTrackPublications.values(),
        Track.Source.Microphone
      );
      const video = selectTrack(
        this.#videoCloud.get(deviceId),
        participant.videoTrackPublications.values(),
        Track.Source.Camera
      );

      changed = updateTrack(this.#audioCloud, deviceId, audio) || changed;
      changed = updateTrack(this.#videoCloud, deviceId, video) || changed;

      // ENG-3716: a track already present at connect time never fires
      // TrackSubscribed — settle the connect wait from reconciliation too.
      if (video) {
        resolveVideoTrackWait(deviceId);
      }
    });

    // Participant removal can precede track-unsubscribe events in LiveKit 2.15.
    for (const deviceId of this.#audioCloud.keys()) {
      if (!activeDeviceIds.has(deviceId)) changed = this.#audioCloud.delete(deviceId) || changed;
    }
    for (const deviceId of this.#videoCloud.keys()) {
      if (!activeDeviceIds.has(deviceId)) changed = this.#videoCloud.delete(deviceId) || changed;
    }

    return changed;
  }

  /** @private */
  #synchronizeLocal(agentId: Uint8Array): boolean {
    const idHex = toHexFromId(agentId)!;
    const room = this.#liveKitRoomsLocal[idHex];

    if (!room || !this.#startedLocalMap[idHex]) return false;

    const activeDeviceIds = new Set<Device['id']>();
    let changed = false;

    // Reconcile only devices owned by this local transport; other agents share the buckets.
    [...room.remoteParticipants.values()].forEach((participant) => {
      const deviceId = toDeviceId(participant.identity);
      const lookedUpId = this.#deviceIdLookup(deviceId);

      if (lookedUpId?.toString() !== agentId.toString()) return;
      activeDeviceIds.add(deviceId);

      // Kind-filtered selection, NOT getTrackPublication(Source.*): that lookup
      // is kind-agnostic (first publication whose `source` matches, in map
      // order) and the encoder publishes the camera AUDIO track with
      // source=CAMERA ahead of the video track — so the video bucket got the
      // audio track, and because this runs on every governor tick it kept
      // re-poisoning the bucket after the kind-correct subscribe handler fixed
      // it. Symptom: videoForAgent hands consumers an audio sid → black tile /
      // 60s "did not advance video time" (CI #1509 run 32904567960,
      // bandwidth-governor:268). Mirrors #synchronizeCloud, which was always
      // kind-filtered and immune. Prefer the already-selected track when its
      // publication is still live so event-selected replacements stick.
      const pickLiveTrack = (
        current: RemoteTrack | undefined,
        publications: Iterable<RemoteTrackPublication>
      ): RemoteTrack | undefined => {
        const candidates = [...publications].filter(
          (publication): publication is RemoteTrackPublication & { track: RemoteTrack } =>
            publication.track !== undefined &&
            !this.#stalePublications.has(publication) &&
            !this.#videoSubscriptionFailures.isRejected(publication)
        );
        return (
          candidates.find((publication) => publication.track === current)?.track ??
          candidates[candidates.length - 1]?.track
        );
      };
      const audio = pickLiveTrack(
        this.#audioLocal.get(deviceId),
        participant.audioTrackPublications.values()
      );
      const video = pickLiveTrack(
        this.#videoLocal.get(deviceId),
        participant.videoTrackPublications.values()
      );

      if (audio && this.#audioLocal.get(deviceId) !== audio) {
        this.#audioLocal.set(deviceId, audio);
        changed = true;
      } else if (!audio) {
        changed = this.#audioLocal.delete(deviceId) || changed;
      }

      if (video && this.#videoLocal.get(deviceId) !== video) {
        this.#videoLocal.set(deviceId, video);
        changed = true;
      } else if (!video) {
        changed = this.#videoLocal.delete(deviceId) || changed;
      }

      // ENG-3716: settle the connect wait for tracks reconciled without a
      // TrackSubscribed event (already present when the local room connected).
      if (video) {
        resolveVideoTrackWait(deviceId);
      }
    });

    for (const deviceId of this.#audioLocal.keys()) {
      if (
        this.#deviceIdLookup(deviceId)?.toString() === agentId.toString() &&
        !activeDeviceIds.has(deviceId)
      ) {
        changed = this.#audioLocal.delete(deviceId) || changed;
      }
    }
    for (const deviceId of this.#videoLocal.keys()) {
      if (
        this.#deviceIdLookup(deviceId)?.toString() === agentId.toString() &&
        !activeDeviceIds.has(deviceId)
      ) {
        changed = this.#videoLocal.delete(deviceId) || changed;
      }
    }

    return changed;
  }

  /**
   * @private ENG-3716: after a room reconnect, a publisher that churned
   * (unpublish + republish) can leave the OLD publication still subscribed
   * alongside the new one. Both then deliver tracks and the source drifts into
   * 'Multiple video tracks detected' instead of showing the current stream.
   *
   * Reconcile by cancelling every subscribed publication whose track is not
   * the bucket-selected current track (the buckets were just reconciled by
   * #synchronize, so they hold the authoritative selection). Resubscription of
   * the current publication is then reapplied by refreshTrackSubscriptions —
   * this method only cancels, it never subscribes.
   */
  #cancelStaleSubscriptions(): void {
    const cancelStaleFor = (
      participant: RemoteParticipant,
      audioBucket: MutableTracks,
      videoBucket: MutableTracks
    ): void => {
      const deviceId = toDeviceId(participant.identity);

      const cancel = (
        publications: Iterable<RemoteTrackPublication>,
        current: T_Maybe<RemoteTrack>
      ): void => {
        // Without an authoritative current track there is nothing to keep —
        // leave the desired-state decision to refreshTrackSubscriptions.
        if (!current) {
          return;
        }

        for (const publication of publications) {
          if (publication.track !== undefined && publication.track !== current) {
            this.#stalePublications.add(publication);
            if (publication.isSubscribed) publication.setSubscribed(false);
          }
        }
      };

      cancel(participant.videoTrackPublications.values(), videoBucket.get(deviceId));
      cancel(participant.audioTrackPublications.values(), audioBucket.get(deviceId));
    };

    this.#liveKitRoomCloud?.remoteParticipants.forEach((participant) => {
      cancelStaleFor(participant, this.#audioCloud, this.#videoCloud);
    });

    for (const idHex of Object.keys(this.#liveKitRoomsLocal)) {
      this.#liveKitRoomsLocal[idHex]?.remoteParticipants.forEach((participant) => {
        cancelStaleFor(participant, this.#audioLocal, this.#videoLocal);
      });
    }
  }

  public localParticipantWithOptions(
    agentId: Uint8Array,
    options?: { mode?: 'local' | 'cloud' | 'hybrid' }
  ) {
    const idHex = toHexFromId(agentId)!;

    if (options?.mode === 'local') {
      return this.#liveKitRoomsLocal[idHex]?.localParticipant;
    }

    if (options?.mode === 'cloud') {
      return this.#liveKitRoomCloud!.localParticipant!;
    }

    return this.localParticipantForAgent(agentId);
  }

  public localParticipantForAgent(agentId: Uint8Array) {
    const idHex = toHexFromId(agentId)!;

    if (this.connectedModeForAgent(agentId) === 'local') {
      return this.#liveKitRoomsLocal[idHex]!.localParticipant;
    }

    return this.#liveKitRoomCloud!.localParticipant;
  }

  public remoteParticipantsWithOptions(
    agentId: Uint8Array,
    options?: { mode?: 'local' | 'cloud' | 'hybrid' }
  ) {
    const idHex = toHexFromId(agentId)!;

    if (options?.mode === 'local') {
      return this.#liveKitRoomsLocal[idHex]?.remoteParticipants ?? new Map();
    }

    if (options?.mode === 'cloud') {
      return this.#liveKitRoomCloud?.remoteParticipants ?? new Map();
    }

    return this.remoteParticipantsForAgent(agentId);
  }

  public inRoom(agentId: Uint8Array, participantId: string): boolean {
    const idHex = toHexFromId(agentId)!;

    // No local room exists for cloud-only agents (embedded, browser, or a
    // client that never started local) — the unguarded `!` here threw a
    // TypeError that killed the presence watcher AND the jest worker running
    // the cloud-liveness suites (#1502 merge seam, CI red 2026-08-25).
    const localRoom = this.#liveKitRoomsLocal[idHex];
    const inLocal =
      !!localRoom &&
      localRoom.state === ConnectionState.Connected &&
      localRoom.remoteParticipants.has(participantId);
    const inCloud =
      this.#liveKitRoomCloud?.state === ConnectionState.Connected &&
      this.#liveKitRoomCloud.remoteParticipants.has(participantId);

    return inLocal || (inCloud ?? false);
  }

  public remoteParticipantsForAgent(agentId: Uint8Array) {
    const idHex = toHexFromId(agentId)!;

    if (this.connectedModeForAgent(agentId) === 'local') {
      return this.#liveKitRoomsLocal[idHex]?.remoteParticipants ?? new Map();
    }

    return this.#liveKitRoomCloud?.remoteParticipants ?? new Map();
  }

  public state(agentId: Uint8Array) {
    const idHex = toHexFromId(agentId)!;
    const localState = this.#liveKitRoomsLocal[idHex]?.state;
    const cloudState = this.#liveKitRoomCloud?.state;

    // S7: state-liveness is OR over both rooms. A live other transport is
    // not reported dead because #mode selected the opposite room.
    if (localState === ConnectionState.Connected || cloudState === ConnectionState.Connected) {
      return ConnectionState.Connected;
    }

    if (
      localState === ConnectionState.Reconnecting ||
      cloudState === ConnectionState.Reconnecting
    ) {
      return ConnectionState.Reconnecting;
    }

    return cloudState ?? localState;
  }

  public onRoomEvent(event: RoomEvent, listener: (...args: any[]) => void, agentKey = '*'): void {
    let listeners = this.#eventHandlers.get(event);

    if (!listeners) {
      listeners = new Map();
      this.#eventHandlers.set(event, listeners);
    }

    // Same-agent re-init replaces; other agents keep their handlers.
    listeners.set(agentKey, listener);
  }

  // Fan-out to every registered agent. Protected so tests can drive the
  // same path production room listeners use.
  protected dispatchRoomEvent(event: RoomEvent, ...args: any[]): void {
    const listeners = this.#eventHandlers.get(event);

    if (!listeners) {
      return;
    }

    for (const listener of listeners.values()) {
      listener(...args);
    }
  }

  /** @private */
  #synchronize(): void {
    let changed = this.#synchronizeCloud();

    for (const localEncoderId of this.#localEncoderIds) {
      changed = this.#synchronizeLocal(localEncoderId) || changed;
    }

    // Periodic reconciliation must publish removals that had no unsubscribe event.
    if (changed) {
      this.emit('changed').catch((error) => {
        logger.critical(error);
      });
    }
  }

  isModePendingForAgent(agentId: Uint8Array): boolean {
    return this.#mode[toHexFromId(agentId)!] !== this.connectedModeForAgent(agentId);
  }

  downstreamNetworkQualityForAgent(agentId: Uint8Array): number {
    const connectionQualityScale = {
      [ConnectionQuality.Unknown]: 0.0001,
      [ConnectionQuality.Lost]: 0.0001,
      [ConnectionQuality.Poor]: 0.25,
      [ConnectionQuality.Good]: 0.5,
      [ConnectionQuality.Excellent]: 1
    };

    const idHex = toHexFromId(agentId)!;

    return connectionQualityScale[
      this.#liveKitRoomsLocal[idHex]!.localParticipant.connectionQuality
    ];
  }

  #agentInRoom: Record<string, boolean> = {};
  #agentInRoomViaPresence: Record<Agent['id'], boolean> = {};
  #localModeRecovery: Record<string, boolean> = {};
  #localRecoveryTimers: Record<string, ReturnType<typeof setTimeout> | undefined> = {};
  // Pace retries per agent; initial recovery is immediate and concurrent triggers share its attempt.
  #localRestartGate = new RestartBackoffGate();
  // Whether THIS episode has already posted its terminal notice. The 120s budget
  // re-runs the loop as a slow heartbeat, but the notice is posted exactly once
  // (dedupe by id). Reset when a fresh episode opens or the episode ends.
  #localRecoveryNotified: Record<string, boolean> = {};

  // Pace recovery through the shared gate. After 120s, post one notice and continue at the
  // 60s ceiling; success or teardown ends the episode. Concurrent triggers join the same attempt.
  #ensureLocalRecoveryLoop(agentId: Uint8Array): void {
    const idHex = toHexFromId(agentId)!;
    if (this.#localRecoveryTimers[idHex]) {
      return;
    }

    const startedAt = this.#localRecoveryStartedAt[idHex];
    if (startedAt === undefined || !this.#localModeRecovery[idHex] || this.#stopped) return;
    const elapsed = Date.now() - startedAt;
    const expired = elapsed >= LOCAL_RECOVERY_BUDGET_MS;
    // Active phase wakes ~10s so the notice posts promptly at the budget; the
    // post-budget heartbeat wakes at the gate's 60s ceiling. Jitter keeps a fleet
    // of studios from stampeding a freshly restarted bridge in lock-step.
    const delay = expired
      ? LOCAL_RESTART_MAX_INTERVAL_MS + Math.random() * 4_000
      : Math.max(0, Math.min(LOCAL_RECOVERY_BUDGET_MS - elapsed, 8_000 + Math.random() * 4_000));
    this.#localRecoveryTimers[idHex] = setTimeout(async () => {
      this.#localRecoveryTimers[idHex] = undefined;
      const current = (): boolean =>
        !this.#stopped &&
        this.#localModeRecovery[idHex] === true &&
        this.#localRecoveryStartedAt[idHex] === startedAt;
      if (!current()) return;
      if (
        Date.now() - startedAt >= LOCAL_RECOVERY_BUDGET_MS &&
        !this.#localRecoveryNotified[idHex]
      ) {
        // Retire any hung attempt and its Room before opening the slow-heartbeat abort scope.
        this.#localRecoveryNotified[idHex] = true;
        this.#localRecoveryAborts[idHex]?.abort();
        for (const controller of this.#localStartAborts[idHex] ?? []) controller.abort();
        void this.#stopLocalInner(agentId).catch((error) => {
          logger.warning('Expired local recovery cleanup failed.', error);
        });
        // Fresh abort scope so the heartbeat's own attempts run after the bound.
        this.#localRecoveryAborts[idHex] = new AbortController();
        // One attempt before posting so an expiry-on-wake with a reachable bridge
        // reconnects without the operator pressing Retry.
        if (this.#localRestartGate.tryAcquire(idHex))
          void this.#rebuildLocalAfterDisconnect(agentId);
        // Resolved here, not at module scope, so this transport carries no UI in
        // its load graph. The dedupe flag above is already set, so awaiting cannot
        // let a second tick post the same notice.
        let RetryButton;
        try {
          RetryButton = await loadRetryButton();
        } catch (error) {
          if (!current()) return;
          // This notice is the only Retry affordance, so a failed load must not
          // end the episode. Reopen the dedupe and let the next tick post.
          logger.warning('Local recovery notice deferred: Retry control unavailable.', error);
          this.#localRecoveryNotified[idHex] = false;
          this.#ensureLocalRecoveryLoop(agentId);
          return;
        }
        // Recovery or teardown can finish while the Retry component loads.
        if (!current()) return;
        // Keep the source/selection owner and healthy cloud leg intact. F10:
        // actionable lights the masthead bell for the only recovery affordance.
        post(
          {
            id: `local-recovery-${idHex}`,
            title: 'Local Bridge connection unavailable',
            severity: 'error',
            actionable: true,
            message: createElement(
              'div',
              null,
              'Still trying to reconnect to the local Bridge. Check the Bridge connection, or retry now.',
              // Use the shared keyboard-focusable notice action.
              createElement(
                RetryButton,
                {
                  'type': 'button',
                  'variant': 'secondary',
                  'size': 'sm',
                  'className': 'focus-visible:ring-primary/50 focus-visible:ring',
                  'aria-label': 'Retry local Bridge connection',
                  'onClick': () => {
                    if (this.#stopped || this.#localRecoveryStartedAt[idHex] !== startedAt) return;
                    // Explicit Retry opens a fresh budget: clear the pending heartbeat
                    // tick, reset the notice dedupe and abort scope, then attempt.
                    clearTimeout(this.#localRecoveryTimers[idHex]);
                    this.#localRecoveryTimers[idHex] = undefined;
                    this.#localRecoveryNotified[idHex] = false;
                    this.#localRecoveryAborts[idHex] = new AbortController();
                    this.#localRecoveryStartedAt[idHex] = Date.now();
                    this.#localModeRecovery[idHex] = true;
                    this.#ensureLocalRecoveryLoop(agentId);
                    void this.#rebuildLocalAfterDisconnect(agentId);
                  }
                },
                'Retry'
              )
            )
          },
          { autoClose: false }
        );
        this.#ensureLocalRecoveryLoop(agentId);
        return;
      }
      // Gate every retry (C2/D3): a denied tick reschedules without attempting, so
      // no external trigger path can bypass the ladder into a storm.
      if (this.#localRestartGate.tryAcquire(idHex)) void this.#rebuildLocalAfterDisconnect(agentId);
      this.#ensureLocalRecoveryLoop(agentId);
    }, delay);
  }

  async #watchAgentPresence() {
    if (this.#stopped) return;
    for (const localEncoderId of this.#localEncoderIds) {
      const idHex = toHexFromId<Agent['id']>(localEncoderId)!;

      const inRoom = this.inRoom(localEncoderId, idHex);

      if (!inRoom) {
        if (this.#agentInRoomViaPresence[idHex]) {
          this.#agentInRoomViaPresence[idHex] = false;
          this.#agentInRoom[idHex] = false;

          this.#handleAgentPresenceChange(localEncoderId);
          continue;
        }
        continue;
      }

      if (this.#agentInRoomViaPresence[idHex]) {
        continue;
      }

      this.#agentInRoom[idHex] = true;
      this.#agentInRoomViaPresence[idHex] = true;

      if (this.#localModeRecovery[idHex]) {
        await this.restartLocalConnection(localEncoderId);
        if (this.#stopped) return;
      }

      this.#handleAgentPresenceChange(localEncoderId);

      // Presence is another caller of the room/account START episode. The
      // LiveKit lifetime releases only this caller, preserving mounted peers.
      void startAgent(idHex, this.id, this.#stopController.signal).catch((error) => {
        logger.warning('Presence START failed.', error);
      });
    }
  }

  agentPresenceListeners: Map<string, (agentId: string, inRoom: boolean) => void> = new Map();

  public onAgentPresence(listener: (agentId: string, inRoom: boolean) => void) {
    const id = generateId();

    this.agentPresenceListeners.set(id, listener);

    return () => {
      this.agentPresenceListeners.delete(id);
    };
  }

  public listCurrentParticipants(encoderID: Uint8Array): string[] {
    if (this.connectedModeForAgent(encoderID) === 'local') {
      const localRoom = this.#liveKitRoomsLocal[toHexFromId(encoderID)!];

      if (localRoom) {
        return Array.from(localRoom.remoteParticipants.keys());
      }
    }

    if (!this.#liveKitRoomCloud) {
      return [];
    }

    return Array.from(this.#liveKitRoomCloud.remoteParticipants.keys());
  }

  presentAgents: Map<string, Date> = new Map();

  async #handleAgentPresenceChange(encoderId: Uint8Array) {
    for (const listener of this.agentPresenceListeners.values()) {
      listener(toHexFromId(encoderId)!, this.#agentInRoom[toHexFromId(encoderId)!] ?? false);
    }

    const idHex = toHexFromId(encoderId)!;

    this.presentAgents.set(idHex, new Date());
  }

  async pingAgent(agentId: Uint8Array): Promise<boolean> {
    const subject = `direct.authenticated.${toHexFromId(agentId)!}.ping`;

    const promise = new Promise<boolean>((resolve) => {
      const timeout = 10_000;
      const interval = 1_000;

      let resolved = false;
      let cumulativeInterval = 0;

      let timeoutTimer: NodeJS.Timeout | undefined;
      let intervalTimer: NodeJS.Timeout | undefined;

      intervalTimer = setInterval(async () => {
        const tickTimeout = timeout - cumulativeInterval;

        if (tickTimeout <= 0) {
          clearInterval(intervalTimer);
          return;
        }

        cumulativeInterval += interval;

        try {
          await nats
            .agentConnection(toHexFromId(agentId)!)
            .request(subject, new Uint8Array([1]), { timeout: tickTimeout });
          if (!resolved) {
            resolve(true);
            clearInterval(intervalTimer);
            clearTimeout(timeoutTimer);
            resolved = true;
          }
        } catch (_) {}
      }, interval);

      timeoutTimer = setTimeout(() => {
        if (!resolved) {
          resolve(false);
          clearInterval(intervalTimer);
          clearTimeout(timeoutTimer);
          resolved = true;
        }
      }, timeout);
    });

    return await promise;
  }
  public restartLocalConnection(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    const room = this.#liveKitRoomsLocal[idHex];
    if (
      this.#stopped ||
      !isElectronApp() ||
      this.#stoppingLocalMap[idHex] ||
      !room ||
      (!this.#startedLocalMap[idHex] && this.#localRecoveryStartedAt[idHex] === undefined) ||
      (room.state === ConnectionState.Connected && !publisherPcManagerClosed(room))
    ) {
      return Promise.resolve();
    }
    if (this.#rebuildingLocal[idHex]) return this.#rebuildingLocal[idHex]!;
    if (this.#localRecoveryStartedAt[idHex] !== undefined) return Promise.resolve();
    // Fresh episode: the first attempt is immediate (this call), so the notice
    // dedupe starts clean and the loop's ticks are what the gate paces.
    this.#localRecoveryNotified[idHex] = false;
    this.#localRecoveryAborts[idHex] = new AbortController();
    this.#localRecoveryStartedAt[idHex] = Date.now();
    this.#localModeRecovery[idHex] = true;
    this.#ensureLocalRecoveryLoop(agentId);
    return this.#rebuildLocalAfterDisconnect(agentId);
  }

  receptionMap: Record<
    string,
    {
      cloud?: Date;
      local?: Date;

      reconnectingLocal?: Date | undefined;
    }
  > = {};

  // Stamps per-transport data arrival. Relative lag between cloud and local
  // data is first-packet-wins noise and must not recycle the local room.
  // Bounded local-video retry is the Disconnected → #rebuildLocal path, which
  // never leaves the local room down terminally.
  private monitorPacketReception(agentID: Uint8Array, mode: 'local' | 'cloud') {
    if (isElectronApp('tray')) {
      return;
    }

    const idHex = toHexFromId(agentID)!;

    if (this.receptionMap[idHex] === undefined) {
      this.receptionMap[idHex] = {};
    }

    this.receptionMap[toHexFromId(agentID)!]![mode] = new Date();

    if (
      mode === 'local' &&
      this.receptionMap[toHexFromId(agentID)!]?.reconnectingLocal !== undefined
    ) {
      this.receptionMap[toHexFromId(agentID)!]!.reconnectingLocal = undefined;
      this.dispatchRoomEvent(RoomEvent.Reconnected);
    }

    const currentRecord = this.receptionMap[toHexFromId(agentID)!];

    if ((currentRecord?.cloud ?? 0) === 0 || (currentRecord?.local ?? 0) === 0) {
      return;
    }

    // S3: relative data-channel lag is meaningless under first-packet-wins.
    // Do not tear the local room as a reaction to state staleness. Video
    // retry is retryLocalVideo / failOverLocalVideoToCloud.
    if (+(currentRecord?.cloud ?? 0) - +(currentRecord?.local ?? 0) > 10_000) {
      logger.info(
        `monitorPacketReception data-lag agent=${toHexFromId(agentID)} cloud-ahead; not tearing local room`
      );
    }
  }

  #markVideoReconnecting(idHex: string, reconnecting: boolean): void {
    this.#videoReconnecting[idHex] = reconnecting;
  }

  #clearVideoReconnecting(idHex: string): void {
    this.#markVideoReconnecting(idHex, false);
    this.#resumeGovernorFor('reconnecting');
    clear('pending-mode-switch-notification');
  }

  // Disconnected after SDK reconnectPolicy exhaustion (and power:resume via
  // onSystemResume). Count consecutive failed rebuilds; after
  // LOCAL_VIDEO_RETRY_LIMIT, fail over video to cloud while the local room
  // keeps trying. Catch so a failed connect is not an unhandled rejection.
  // Single-flight per agent: a second trigger while one attempt is in flight
  // joins it (the paced #ensureLocalRecoveryLoop retries after failure), so a
  // Disconnected + resume pair cannot double-count a failure.
  // Per rule: settle-every-terminal-branch (ai.knowledge/global) — the guard
  // is released in finally on success and on every thrown path.
  #rebuildLocalAfterDisconnect(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    const existing = this.#rebuildingLocal[idHex];
    if (existing) return existing;
    const startedAt = this.#localRecoveryStartedAt[idHex];
    const controller = this.#localRecoveryAborts[idHex];
    if (!controller || controller.signal.aborted) return Promise.resolve();
    const current = (): boolean =>
      !this.#stopped &&
      this.#localModeRecovery[idHex] === true &&
      this.#localRecoveryStartedAt[idHex] === startedAt &&
      this.#rebuildingLocal[idHex] === run;
    const run = Promise.resolve()
      .then(async () => {
        if (!current()) return;
        try {
          const room = this.#liveKitRoomsLocal[idHex];
          if (room?.state === ConnectionState.Connected && publisherPcManagerClosed(room)) {
            // This is affected-leg SDK evidence, not a liveness verdict or silence.
            await this.#waitForLocalOperation(this.#stopLocalInner(agentId), controller.signal);
            if (!current()) return;
          }
          await this.#waitForLocalOperation(this.startLocal(agentId), controller.signal);
          if (!current() || !this.isLocalRoomConnected(agentId)) return;
          this.#localRebuildFailures[idHex] = 0;
          this.#localModeRecovery[idHex] = false;
          this.#localRecoveryNotified[idHex] = false;
          this.#localRecoveryStartedAt[idHex] = undefined;
          clearTimeout(this.#localRecoveryTimers[idHex]);
          this.#localRecoveryTimers[idHex] = undefined;
          clear(`local-recovery-${idHex}`);
          if (this.#rebuildingLocal[idHex] === run) this.#rebuildingLocal[idHex] = undefined;
          if (!this.#videoFailedOver[idHex]) {
            void this.setMode('local', agentId, false, true).catch((error) => {
              logger.warning('Recovered local mode selection failed.', error);
            });
          }
        } catch (error) {
          if (!current()) return;
          logger.warning(`local rebuild after disconnect failed agent=${idHex}: ${String(error)}`);
          this.#localRebuildFailures[idHex] = (this.#localRebuildFailures[idHex] ?? 0) + 1;
          if (
            this.#localRebuildFailures[idHex]! >= LOCAL_VIDEO_RETRY_LIMIT &&
            !this.#videoFailedOver[idHex]
          ) {
            // Mode publication can wait on an encoder mutation. It does not
            // own completion of this failed local connection attempt.
            void this.failOverLocalVideoToCloud(agentId).catch((error) => {
              logger.warning('Local video failover could not complete.', error);
            });
          }
        }
      })
      .catch((error) => {
        if (current()) logger.warning('Local recovery could not complete.', error);
      })
      .finally(() => {
        // A cancelled old flight cannot release a later generation's reservation.
        if (this.#rebuildingLocal[idHex] === run) this.#rebuildingLocal[idHex] = undefined;
      });
    this.#rebuildingLocal[idHex] = run;
    return run;
  }

  public isVideoReconnecting(agentId: Uint8Array): boolean {
    return !!this.#videoReconnecting[toHexFromId(agentId)!];
  }

  public isLocalRoomConnected(agentId: Uint8Array): boolean {
    const idHex = toHexFromId(agentId)!;
    return this.#liveKitRoomsLocal[idHex]?.state === ConnectionState.Connected;
  }

  public isLocalRoomStarted(agentId: Uint8Array): boolean {
    return !!this.#startedLocalMap[toHexFromId(agentId)!];
  }

  #postVideoReconnectingToast(): void {
    post(
      {
        title: 'Attempting to connect to Cloud Mode...',
        message: 'Your sources are reconnecting. This may take a few moments.',
        severity: 'info',
        id: 'pending-mode-switch-notification'
      },
      { autoClose: false }
    );
  }

  /**
   * Bounded local-video retry. Recycles the local room for a window while
   * cloud state covers delivery. Does not emit state.error. After the attempt
   * budget, media fails over to cloud and the local room is rebuilt if down.
   */
  public async retryLocalVideo(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    this.#markVideoReconnecting(idHex, true);
    this.#suspendGovernorFor('reconnecting');
    this.#postVideoReconnectingToast();

    for (let attempt = 1; attempt <= LOCAL_VIDEO_RETRY_LIMIT; attempt++) {
      this.#videoRetryCount[idHex] = attempt;
      logger.info(`retryLocalVideo agent=${idHex} attempt=${attempt}`);
      try {
        await this.stopLocal(agentId);
        await this.startLocal(agentId);
      } catch (error) {
        logger.warning(`retryLocalVideo startLocal failed agent=${idHex}: ${String(error)}`);
      }

      if (this.isLocalRoomConnected(agentId)) {
        this.#clearVideoReconnecting(idHex);
        return;
      }
    }

    await this.failOverLocalVideoToCloud(agentId);
  }

  /**
   * Terminal video failover: #mode selects cloud media; the local LiveKit
   * room stays connected (or is rebuilt via acquireLocalConnectionWithRetry).
   * Never stopLocal as the permanent failover. Never write depot offline.
   */
  public async failOverLocalVideoToCloud(agentId: Uint8Array): Promise<void> {
    const idHex = toHexFromId(agentId)!;
    const room = this.#liveKitRoomsLocal[idHex];
    const recovering = this.#localModeRecovery[idHex];
    this.#videoFailedOver[idHex] = true;
    this.#markVideoReconnecting(idHex, true);
    this.#suspendGovernorFor('reconnecting');
    this.#postVideoReconnectingToast();
    logger.info(`failOverLocalVideoToCloud agent=${idHex}`);

    // auto=true: video failover is machinery, not an operator unlock of the
    // admin padlock (ENG-4665 H2/M3). Encoder mutate still no-ops if
    // #lockedLocal is set.
    await this.setMode('cloud', agentId, false, true);
    if (this.#stopped || room !== this.#liveKitRoomsLocal[idHex]) return;

    if (!this.isLocalRoomConnected(agentId) && !recovering) {
      this.#startedLocalMap[idHex] = undefined;
      try {
        await this.startLocal(agentId);
      } catch (error) {
        logger.warning(`failOverLocalVideoToCloud rebuild failed agent=${idHex}: ${String(error)}`);
      }
    }

    this.#clearVideoReconnecting(idHex);
  }

  // ── Bandwidth governor integration ────────────────────────────────────────

  async #runSubscriptionAndGovernorCycle(): Promise<void> {
    try {
      if (!this.#bandwidthGovernorEnabled) {
        void this.refreshTrackSubscriptionsForAll();
        this.#synchronize();
        return;
      }

      await this.refreshTrackSubscriptionsForAll();
      this.#synchronize();
      this.#runGovernorTick();
    } catch (error) {
      logger.critical(error instanceof Error ? error : String(error));
    }
  }

  #runGovernorTick(): void {
    if (!this.#governor) return;

    const publications = this.#governorPublications();
    this.#governor.setActiveDevices(new Set(publications.keys()));
    this.#reconcileGovernorPublicationIdentities(publications);
    this.#governor.setVisibleDevices(getVisibleDevices(this.id));
    for (const deviceId of this.#governorHealthGenerations.keys()) {
      if (!publications.has(deviceId)) {
        this.#governorHealthGenerations.delete(deviceId);
        this.#governorSampleFloor.delete(deviceId);
      }
    }

    if (this.#governorSuppressionReasons.size > 0) {
      // Rate-limited (on-change) skip stamp: a suppressed tick is otherwise
      // invisible, and 30s of silent skips read as "governor ignored the
      // toggle" (CI #1486 job 98003236577).
      const skipNote = `[bwgov] tick skipped: suppressed=[${[...this.#governorSuppressionReasons].join(',')}] manual=${this.#governor?.isManualLowBandwidthMode() ?? false}`;
      if (skipNote !== this.#lastGovernorSkipNote) {
        this.#lastGovernorSkipNote = skipNote;
        logger.info(skipNote);
      }
      return;
    }
    this.#lastGovernorSkipNote = undefined;

    const managers = videoStatsManagersForRoom(this.id);
    for (const [deviceId, publication] of publications) {
      const manager = managers.get(deviceId);
      if (!manager || !manager.stats.ready || manager.track !== publication.track) {
        continue;
      }

      const trackStreamState = publication.track?.streamState;
      const sampleGeneration = this.#governorHealthGeneration(deviceId, manager, publication.track);
      const sampleFloor = this.#governorSampleFloor.get(deviceId);
      if (sampleFloor !== undefined && sampleGeneration <= sampleFloor) {
        continue;
      }
      this.#governorSampleFloor.delete(deviceId);

      this.#governor.updateTrackHealth(deviceId, {
        bitrate: manager.stats.bitrate,
        quality: manager.stats.quality,
        streamState:
          trackStreamState === Track.StreamState.Paused
            ? 'paused'
            : trackStreamState === Track.StreamState.Active
              ? 'playing'
              : 'unknown',
        canSetLowQuality: this.#publicationHasLowLayer(publication),
        sampleGeneration
      });
    }

    const previousState = this.#governor.getState();
    const selectedActions = this.#governor.onSelectedDeviceChanged(
      this.#selectedVideoDeviceId,
      this.#selectedVideoDeviceId
    );
    const actions = selectedActions.length > 0 ? selectedActions : this.#governor.evaluate();
    this.#applyGovernorActions(actions, publications);
    this.#emitGovernorStateEdge(previousState);
  }

  #governorPublications(): Map<Device['id'], RemoteTrackPublication> {
    const publications = new Map<Device['id'], RemoteTrackPublication>();
    this.#liveKitRoomCloud?.remoteParticipants.forEach((participant) => {
      const deviceId = participant.identity as Device['id'];
      const proxy = locateProxy(deviceId);
      const agentId = proxy ? toIdFromHex(proxy.id) : undefined;
      if (!agentId || this.connectedModeForAgent(agentId) !== 'cloud') {
        return;
      }

      participant.videoTrackPublications.forEach((publication) => {
        if (publication.track && this.#videoCloud.get(deviceId) === publication.track) {
          publications.set(deviceId, publication);
        }
      });
    });
    return publications;
  }

  #reconcileGovernorPublicationIdentities(
    publications: ReadonlyMap<Device['id'], RemoteTrackPublication>
  ): void {
    if (!this.#governor) return;

    for (const deviceId of this.#governorPublicationIdentities.keys()) {
      if (!publications.has(deviceId)) {
        this.#governorPublicationIdentities.delete(deviceId);
      }
    }

    for (const [deviceId, publication] of publications) {
      const previous = this.#governorPublicationIdentities.get(deviceId);
      if (previous?.publication === publication && previous.track === publication.track) {
        continue;
      }

      const state = this.#governor.getDeviceState(deviceId);
      const action: GovernorAction | null =
        state === 'quality-capped'
          ? { type: 'set-quality', deviceId, quality: VideoQuality.LOW }
          : state === 'paused-auto' || state === 'paused-manual'
            ? { type: 'set-enabled', deviceId, enabled: false }
            : null;

      // A republished track must inherit policy before fresh health can affect the next decision.
      if (action && !this.#applyGovernorAction(action, publication)) {
        continue;
      }
      this.#governorPublicationIdentities.set(deviceId, {
        publication,
        track: publication.track
      });
    }
  }

  #publicationHasLowLayer(publication: RemoteTrackPublication): boolean {
    return Boolean(
      publication.trackInfo?.layers?.some((layer) => layer.quality === VideoQuality.LOW)
    );
  }

  #governorHealthGeneration(
    deviceId: Device['id'],
    manager: StatsManager,
    track: RemoteTrack | undefined
  ): number {
    const streamState = track?.streamState;
    const current = this.#governorHealthGenerations.get(deviceId);
    if (!current) {
      this.#governorHealthGenerations.set(deviceId, {
        track,
        statsGeneration: manager.sampleGeneration,
        streamState,
        generation: 1
      });
      return 1;
    }

    if (
      current.track !== track ||
      current.statsGeneration !== manager.sampleGeneration ||
      current.streamState !== streamState
    ) {
      current.track = track;
      current.statsGeneration = manager.sampleGeneration;
      current.streamState = streamState;
      current.generation += 1;
    }
    return current.generation;
  }

  #applyGovernorActions(
    actions: GovernorAction[],
    publications: Map<Device['id'], RemoteTrackPublication> = this.#governorPublications()
  ): void {
    if (!this.#governor) return;

    for (const action of actions) {
      const stateBefore = this.#governor.getDeviceState(action.deviceId);
      const publication = publications.get(action.deviceId);
      if (!publication) {
        if (action.proposalId !== undefined) {
          this.#governor.reject(action);
        }
        continue;
      }

      const applied = this.#applyGovernorAction(action, publication);
      const settled =
        action.proposalId === undefined
          ? applied
          : applied
            ? this.#governor.commit(action)
            : this.#governor.reject(action);

      if (!applied || !settled || action.proposalId === undefined) {
        continue;
      }

      const state = this.#governor.getDeviceState(action.deviceId);
      this.emit('bandwidthDeviceStateChanged', {
        deviceId: action.deviceId,
        state
      }).catch((error) => logger.critical(error));
      this.#captureGovernorAction(action, publication, stateBefore);
    }
  }

  #applyGovernorAction(action: GovernorAction, publication: RemoteTrackPublication): boolean {
    try {
      switch (action.type) {
        case 'set-quality': {
          if (action.quality === VideoQuality.LOW && !this.#publicationHasLowLayer(publication)) {
            return false;
          }
          // Recovery can target 'quality-capped' from a paused state (auto-recover
          // after a quality-capped -> paused-auto degradation, or a manual restore
          // whose snapshot was quality-capped). setVideoQuality alone would leave
          // the publication disabled — the governor would then believe the feed
          // recovered while the video stays paused — so re-enable it first,
          // mirroring the 'restore' case below.
          const currentState = this.#governor?.getDeviceState(action.deviceId) ?? 'normal';
          if (currentState === 'paused-auto' || currentState === 'paused-manual') {
            publication.setEnabled(true);
            if (!restoreLiveKit215AdaptiveStreamControl(publication)) {
              publication.setEnabled(false);
              return false;
            }
          }
          publication.setVideoQuality(action.quality);
          return true;
        }
        case 'set-enabled':
          publication.setEnabled(action.enabled);
          if (action.enabled && !restoreLiveKit215AdaptiveStreamControl(publication)) {
            publication.setEnabled(false);
            return false;
          }
          return true;
        case 'restore': {
          const currentState = this.#governor?.getDeviceState(action.deviceId) ?? 'normal';
          if (currentState === 'paused-auto' || currentState === 'paused-manual') {
            publication.setEnabled(true);
            if (!restoreLiveKit215AdaptiveStreamControl(publication)) {
              publication.setEnabled(false);
              return false;
            }
          }
          publication.setVideoQuality(VideoQuality.HIGH);
          return true;
        }
      }
    } catch {
      logger.warning('Failed to apply a bandwidth governor publication action.');
      return false;
    }
  }

  #captureGovernorAction(
    action: GovernorAction,
    publication: RemoteTrackPublication,
    previousState: DeviceGovernorState
  ): void {
    const health = this.#governor?.getTrackHealth(action.deviceId);
    const source =
      action.deviceId === this.#selectedVideoDeviceId
        ? 'selection'
        : previousState === 'paused-manual' || this.#governor?.isManualLowBandwidthMode()
          ? 'manual'
          : 'automatic';
    const actionType =
      action.type === 'set-quality'
        ? 'quality_cap'
        : action.type === 'restore'
          ? 'restore'
          : action.enabled
            ? 'enable'
            : 'pause';

    try {
      // Identity + brand/model cohort props enable per-camera churn attribution; the
      // registry can miss a device mid-teardown, so fall back to the raw id.
      const deviceContext = resolveDeviceAnalyticsContext(action.deviceId) ?? {
        device_id: action.deviceId
      };
      PostHogManager.getInstance().capture(BANDWIDTH_GOVERNOR_ANALYTICS.ACTION_TAKEN, {
        ...deviceContext,
        source,
        action: actionType,
        reason: health?.reasons[0] ?? 'none',
        tier:
          action.deviceId === this.#selectedVideoDeviceId
            ? 'selected'
            : getVisibleDevices(this.id).has(action.deviceId)
              ? 'visible'
              : 'offscreen',
        affected_count: 1,
        active_count: this.#governor?.getDeviceIds().length ?? 0,
        low_layer_available: this.#publicationHasLowLayer(publication),
        // Post-commit depth (capture runs after settle) — with device_id, makes
        // LIFO recovery order provable from analytics alone.
        stack_depth: this.#governor?.getTransitionStackDepth() ?? 0
      });
    } catch {
      // Analytics never owns the media-control path.
    }
  }

  #emitGovernorStateEdge(previousState: 'normal' | 'congested'): void {
    if (!this.#governor) return;
    const nextState = this.#governor.getState();
    if (nextState === previousState) return;

    this.emit('bandwidthGovernorStateChanged', nextState).catch((error) => {
      logger.critical(error);
    });
    try {
      PostHogManager.getInstance().capture(BANDWIDTH_GOVERNOR_ANALYTICS.STATE_CHANGED, {
        from_state: previousState,
        to_state: nextState
      });
    } catch {
      // Analytics never owns the media-control path.
    }
  }

  // ── Bandwidth governor public API ──────────────────────────────────────────

  public setSelectedVideoDevice(deviceId: Device['id'] | null): void {
    if (!this.#governor || deviceId === this.#selectedVideoDeviceId) return;
    const previousId = this.#selectedVideoDeviceId;
    this.#selectedVideoDeviceId = deviceId;
    const actions = this.#governor.onSelectedDeviceChanged(deviceId, previousId);
    this.#applyGovernorActions(actions);
    this.#runGovernorTick();
  }

  public getBandwidthAttachmentDiagnostic() {
    return bandwidthAttachmentDiagnostic(
      this.#liveKitRoomCloud?.state,
      this.#liveKitRoomCloud?.remoteParticipants ?? new Map()
    );
  }

  public setViewportVisibility(_deviceId: Device['id'], _visible: boolean): void {
    // The room-scoped IntersectionObserver is the sole visibility authority.
  }

  public setManualLowBandwidthMode(enabled: boolean): void {
    if (!this.#governor || this.#governor.isManualLowBandwidthMode() === enabled) {
      return;
    }

    this.#governor.setManualLowBandwidthMode(enabled);
    this.emit('bandwidthManualModeChanged', enabled).catch((error) => logger.critical(error));
    try {
      PostHogManager.getInstance().capture(BANDWIDTH_GOVERNOR_ANALYTICS.MANUAL_MODE_TOGGLED, {
        enabled
      });
    } catch {
      // Analytics never owns the media-control path.
    }
    this.#runGovernorTick();
  }

  public isManualLowBandwidthMode(): boolean {
    return this.#governor?.isManualLowBandwidthMode() ?? false;
  }

  public getBandwidthGovernorState(): 'normal' | 'congested' | 'disabled' {
    return this.#governor?.getState() ?? 'disabled';
  }

  public getBandwidthDeviceState(deviceId: Device['id']): DeviceGovernorState {
    return this.#governor?.getDeviceState(deviceId) ?? 'normal';
  }

  #suspendGovernorFor(reason: GovernorSuppressionReason): void {
    if (!this.#governor) return;
    const wasEmpty = this.#governorSuppressionReasons.size === 0;
    this.#governorSuppressionReasons.add(reason);
    // Suppression transitions are the whole story of the zombie-governor
    // class (suppressed ticks are silent) — one line per edge names it.
    logger.info(
      `[bwgov] suppress +${reason} (held=[${[...this.#governorSuppressionReasons].join(',')}])`
    );
    if (wasEmpty) {
      this.#governor.onBlockAllVideo();
    }
  }

  #resumeGovernorFor(reason: GovernorSuppressionReason): void {
    if (!this.#governor) return;
    const removed = this.#governorSuppressionReasons.delete(reason);
    if (removed) {
      logger.info(
        `[bwgov] suppress -${reason} (held=[${[...this.#governorSuppressionReasons].join(',')}])`
      );
    }
    if (!removed || this.#governorSuppressionReasons.size !== 0) return;

    this.#captureGovernorSampleFloor();
    const reapplyActions = this.#governor.onUnblockAllVideo();
    this.#applyGovernorActions(reapplyActions);
    this.#runGovernorTick();
  }

  #captureGovernorSampleFloor(): void {
    this.#governorSampleFloor.clear();
    const publications = this.#governorPublications();
    for (const [deviceId, manager] of videoStatsManagersForRoom(this.id)) {
      const publication = publications.get(deviceId);
      if (!publication || publication.track !== manager.track) continue;
      this.#governorSampleFloor.set(
        deviceId,
        this.#governorHealthGeneration(deviceId, manager, publication.track)
      );
    }
  }

  #queueGovernorReconnectRecovery(): void {
    this.#governorReconnectSequence = this.#governorReconnectSequence
      .catch(() => undefined)
      .then(async () => {
        if (this.#stopped) return;
        this.#synchronize();
        // ENG-3716: after a reconnect, cancel subscriptions left on stale
        // (pre-churn) publications BEFORE reapplying the desired subscription
        // state — otherwise old+new publications stay subscribed and the
        // source drifts into 'Multiple video tracks detected'.
        this.#cancelStaleSubscriptions();
        this.#watchAgentPresence();
        await this.refreshTrackSubscriptionsForAll();
        if (this.#stopped) return;
        this.#resumeGovernorFor('disconnected');
        this.#resumeGovernorFor('reconnecting');
      })
      .catch((error) => {
        logger.critical(error);
      });
  }

  #normalizeGovernorAfterDisconnect(): void {
    if (!this.#governor) return;

    const previousState = this.#governor.getState();
    const manualMode = this.#governor.isManualLowBandwidthMode();
    const deviceIds = this.#governor.getDeviceIds();
    this.#suspendGovernorFor('disconnected');
    this.#governor.destroy();
    this.#governor = new BandwidthGovernor();
    this.#governor.setSelectedDeviceId(this.#selectedVideoDeviceId);
    this.#governor.onBlockAllVideo();
    this.#governorSampleFloor.clear();
    this.#governorHealthGenerations.clear();
    this.#governorPublicationIdentities.clear();

    for (const deviceId of deviceIds) {
      this.emit('bandwidthDeviceStateChanged', {
        deviceId: deviceId as Device['id'],
        state: 'normal'
      }).catch((error) => logger.critical(error));
    }
    if (previousState !== 'normal') {
      this.emit('bandwidthGovernorStateChanged', 'normal').catch((error) => logger.critical(error));
    }
    if (manualMode) {
      this.emit('bandwidthManualModeChanged', false).catch((error) => logger.critical(error));
    }
  }

  #clearGovernorResources(): void {
    this.#stopped = true;
    if (this.#subscriptionGovernorInterval) {
      clearInterval(this.#subscriptionGovernorInterval);
      this.#subscriptionGovernorInterval = null;
    }
    if (this.#metadataInterval) {
      clearInterval(this.#metadataInterval);
      this.#metadataInterval = null;
    }
    if (this.#forceVideoReattachTimeout) {
      clearTimeout(this.#forceVideoReattachTimeout);
      this.#forceVideoReattachTimeout = null;
    }
    clearTimeout(this.#focusListenerTimeout);
    window.removeEventListener('visibilitychange', this.#focusListener);
    // Interface-change resume listener (Electron only); see the constructor.
    this.#networkChangeUnsubscribe?.();
    this.#networkChangeUnsubscribe = undefined;
    if (this.#visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.#visibilityChangeListener);
      this.#visibilityChangeListener = null;
    }

    clearRoomTrackStats(this.id);
    clearViewportRoom(this.id);
    this.#governor?.destroy();
    this.#governor = null;
    this.#governorSuppressionReasons.clear();
    this.#governorSampleFloor.clear();
    this.#governorHealthGenerations.clear();
    this.#governorPublicationIdentities.clear();
  }

  // Pacing lives on the LiveKit instance, not React state: every resubscribe
  // mints a new mediaStreamID and remounts the tile, so a component-held
  // counter would reset each cycle and never brake the loop.
  #stallResets = new Map<Device['id'], { lastResetAt: number; nextIntervalMs: number }>();

  resetDeviceCloudSubscription(deviceId: Device['id']): void {
    // Recovery is also a new subscription; check again in its delayed callback.
    if (isTrialCameraLockedDevice(this.id, deviceId)) return;
    if (this.videoBlocked) {
      return;
    }

    // Stall recovery stays available for a failed-over agent's cloud track.
    // A denied cloud leg skips only the cloud reset: the local leg below must
    // still recover in local mode, where cloud video is denied by design.
    let cloudAllowed = true;
    if (this.#cloudVideoPolicy === 'deny') {
      const proxy = locateProxy(deviceId);
      cloudAllowed = !!proxy && this.#cloudVideoAllowedForAgent(toIdFromHex(proxy.id)!);
    }

    const now = Date.now();
    const prior = this.#stallResets.get(deviceId);
    // A long quiet stretch means the last stall recovered; restore the fast
    // first response instead of punishing the next unrelated stall.
    const consecutive =
      prior !== undefined && now - prior.lastResetAt < STALL_RESET_BACKOFF_DECAY_MS;
    const intervalMs = consecutive ? prior.nextIntervalMs : STALL_RESET_BASE_INTERVAL_MS;

    // Under congestion a reset is mostly self-harm (keyframe burst on a
    // starved link, re-broken governor exit window). Floor the cadence rather
    // than blocking outright so a genuinely wedged track still recovers.
    const congested = this.#governor?.getState() === 'congested';
    const minIntervalMs = congested
      ? Math.max(intervalMs, STALL_RESET_CONGESTED_MIN_INTERVAL_MS)
      : intervalMs;

    if (prior && now - prior.lastResetAt < minIntervalMs) {
      return;
    }

    let didReset = false;
    const room = cloudAllowed ? this.#liveKitRoomCloud : undefined;
    room?.remoteParticipants.forEach((participant) => {
      participant.videoTrackPublications.forEach((pub) => {
        const deviceID = (participant.identity ?? '') as Device['id'];

        if (deviceID.toLowerCase() !== deviceId.toLowerCase()) {
          return;
        }

        if (!this.#canSubscribeVideo(room!, participant, pub)) return;
        didReset = true;
        pub.setSubscribed(false);

        setTimeout(() => {
          // A delayed reset belongs to this room, never its replacement.
          if (this.#stopped || this.#liveKitRoomCloud !== room) return;
          const blocked = this.videoBlocked;
          pub.setSubscribed(
            !blocked &&
              !this.#stalePublications.has(pub) &&
              this.#canSubscribeVideo(room!, participant, pub) &&
              !isTrialCameraLockedDevice(this.id, deviceID)
          );
        }, 500);
      });
    });

    // In local mode the tile plays the agent's local-room track. After a link
    // bounce (DHCP renew / ICE restart) Chromium's HEVC decoder can wedge: RTP
    // keeps arriving, frames stop decoding and PLIs go unanswered until a fresh
    // subscription builds a new decoder. Resetting only the cloud leg left that
    // tile frozen until reload. Cycle the
    // device's subscribed local publication too and restore it to the current
    // local subscription policy.
    for (const localRoom of Object.values(this.#liveKitRoomsLocal)) {
      localRoom?.remoteParticipants.forEach((participant) => {
        const deviceID = (participant.identity ?? '') as Device['id'];
        if (deviceID.toLowerCase() !== deviceId.toLowerCase()) return;
        participant.videoTrackPublications.forEach((pub) => {
          if (!pub.isSubscribed) return;
          didReset = true;
          pub.setSubscribed(false);

          setTimeout(() => {
            // A delayed reset belongs to this local room, never its replacement.
            if (this.#stopped || !Object.values(this.#liveKitRoomsLocal).includes(localRoom)) {
              return;
            }
            pub.setSubscribed(
              this.localTrackSubscriptionMap[deviceID] === true &&
                !this.videoBlocked &&
                !this.#stalePublications.has(pub) &&
                this.#canSubscribeVideo(localRoom, participant, pub) &&
                !isTrialCameraLockedDevice(this.id, deviceID)
            );
          }, 500);
        });
      });
    }

    // Backoff advances only on executed resets, so a device with no matching
    // publication keeps its fast response for when the track appears. Doubling
    // applies only to consecutive resets; a fresh incident restarts at base.
    if (didReset) {
      this.#stallResets.set(deviceId, {
        lastResetAt: now,
        nextIntervalMs: consecutive
          ? Math.min(intervalMs * 2, STALL_RESET_MAX_INTERVAL_MS)
          : STALL_RESET_BASE_INTERVAL_MS
      });
    }
  }
}
